import { useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode, type UIEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CoachStudioLanding from './welcome/coach-studio/CoachStudioLanding';
import {
  useAuth,
  useCommunityDashboardSnapshot,
  useHomeDashboard,
  useMyProsAnalytics,
  useMyProsBusinesses,
  useRequestCoachWorkspace,
  useScheduleDashboard,
  useScheduleOccurrenceDetail,
  useScheduleSlotFillers,
  useTenantBootstrap,
  useTenantDirectory,
  useTenantWorkDashboard,
  useUpdateWebProfile,
  useWebProfile,
  useWelcomeBuzzRail,
} from '@tark/shared/hooks';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@tark/shared/components';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Building2,
  Calendar,
  CheckCircle2,
  ClipboardList,
  Clock3,
  KeyRound,
  Loader2,
  Medal,
  Newspaper,
  PlayCircle,
  Radio,
  ShieldCheck,
  Sparkles,
  UserPlus,
  Users,
  Wrench,
} from 'lucide-react';
import { AppPageFrame } from '../components/AppPageFrame';
import { hasTenantWorkspaceContext, isCommunityWelcomeRequest } from '../lib/workspaceContext';
import type {
  BuzzFeedV1Block,
  BuzzFeedV1Item,
  BuzzFeedV1Response,
  BuzzLiveRefreshResponse,
  CommunityDashboardSquadPreview,
  CommunityDashboardSnapshotResponse,
  HomeDashboardPriority,
  HomeDashboardProviderEntry,
  ProviderWorkspaceStatus,
  ProviderProfile,
  TenantMembershipSummary,
} from '@tark/shared/types';

type Metric = {
  label: string;
  value: string;
  description: string;
};

type QuickLink = {
  label: string;
  description: string;
  to?: string;
  onClick?: () => void;
  icon: ComponentType<{ className?: string }>;
};

type WelcomeTone = 'community' | 'coach' | 'stringer' | 'academy' | 'workspace' | 'pro';
type WelcomeMode = 'COMMUNITY' | 'COACH' | 'STRINGER' | 'ACADEMY' | 'WORKSPACE';

type WelcomeAction = QuickLink & {
  value?: string | number;
};

type WelcomeModeOption = {
  mode: WelcomeMode;
  label: string;
  tone: WelcomeTone;
  active: boolean;
  disabled?: boolean;
  onSelect: () => void;
};

const COMMUNITY_BUZZ_RAIL_MAX_PAGES = 3;
const HOTLINK_BLOCKED_BUZZ_IMAGE_HOSTS = new Set(['cdn1.tennishead.net']);

function formatCodeLabel(value?: string | null, fallback: string = ''): string {
  if (!value) {
    return fallback;
  }

  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatRole(roleCode?: string | null): string {
  return formatCodeLabel(roleCode, 'Member');
}

function isManageableSquadRole(roleCode?: string | null): boolean {
  switch ((roleCode || '').toUpperCase()) {
    case 'OWNER':
    case 'ADMIN':
    case 'MODERATOR':
      return true;
    default:
      return false;
  }
}

function squadRoleChipClasses(roleCode?: string | null): string {
  switch ((roleCode || '').toUpperCase()) {
    case 'OWNER':
    case 'ADMIN':
      return 'border-primary/20 bg-primary/10 text-primary';
    case 'MODERATOR':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    default:
      return 'border-border bg-surface-elevated/40 text-text-muted';
  }
}

function getCommunitySquadActionLabel(roleCode?: string | null): string {
  return isManageableSquadRole(roleCode) ? 'Manage' : 'Open';
}

function formatProviderTypes(providerTypes: string[] | undefined): string {
  if (!providerTypes || providerTypes.length === 0) {
    return 'Provider';
  }

  return providerTypes
    .map((type) => {
      switch (type) {
        case 'COACH':
          return 'Coach';
        case 'STRINGER':
          return 'Stringer';
        case 'ACADEMY':
          return 'Academy';
        default:
          return type;
      }
    })
    .join(', ');
}

function formatProviderWorkflowState(workflowState?: string | null): string {
  switch (workflowState) {
    case 'DRAFT':
      return 'Draft';
    case 'IN_REVIEW':
      return 'In Review';
    case 'APPROVED':
      return 'Approved';
    case 'LIVE':
      return 'Live';
    case 'PAUSED':
      return 'Paused';
    case 'SUSPENDED':
      return 'Suspended';
    case 'ARCHIVED':
      return 'Archived';
    default:
      return 'Community';
  }
}

function formatWorkspaceStatus(status?: ProviderWorkspaceStatus | string | null): string {
  switch (status) {
    case 'REQUESTED':
      return 'Requested';
    case 'APPROVED':
      return 'Approved';
    case 'ENABLED':
      return 'Enabled';
    case 'DECLINED':
      return 'Declined';
    case 'CANCELLED':
      return 'Cancelled';
    case 'NOT_REQUESTED':
    default:
      return 'Not enabled';
  }
}

function isCoachProvider(
  provider?: HomeDashboardProviderEntry | null,
  priority?: HomeDashboardPriority | null,
): boolean {
  return priority === 'COACH' || Boolean(provider?.providerTypes.includes('COACH'));
}

function getProviderTone(
  provider?: HomeDashboardProviderEntry | null,
  priority?: HomeDashboardPriority | null,
): WelcomeTone {
  if (priority === 'STRINGER' || provider?.providerTypes.includes('STRINGER')) {
    return 'stringer';
  }
  if (priority === 'ACADEMY' || provider?.providerTypes.includes('ACADEMY')) {
    return 'academy';
  }
  if (priority === 'COACH' || provider?.providerTypes.includes('COACH')) {
    return 'coach';
  }
  return 'pro';
}

function getWelcomeToneClasses(tone: WelcomeTone) {
  switch (tone) {
    case 'community':
      return {
        accent: 'bg-sky-500',
        chip: 'border-sky-200 bg-sky-50 text-sky-700',
        icon: 'bg-sky-50 text-sky-700',
        now: 'border-sky-200 bg-white',
        nowSoft: 'border-sky-100 bg-sky-50/70',
        nowLabel: 'text-sky-700',
        nowTitle: 'text-text',
        nowText: 'text-text-muted',
        nowBadge: 'border-sky-200 bg-sky-50 text-sky-700',
        nowTrackActive: 'bg-sky-500',
        nowTrackInactive: 'bg-sky-100',
        nowTile: 'border-sky-100 bg-sky-50/70',
        nowGhost: 'text-sky-700 hover:bg-sky-50',
        button: 'text-sky-700',
      };
    case 'coach':
      return {
        accent: 'bg-primary',
        chip: 'border-primary/20 bg-primary/10 text-primary',
        icon: 'bg-primary/10 text-primary',
        now: 'border-primary/20 bg-white',
        nowSoft: 'border-primary/10 bg-primary/5',
        nowLabel: 'text-primary',
        nowTitle: 'text-text',
        nowText: 'text-text-muted',
        nowBadge: 'border-primary/20 bg-primary/10 text-primary',
        nowTrackActive: 'bg-primary',
        nowTrackInactive: 'bg-primary/10',
        nowTile: 'border-primary/10 bg-primary/5',
        nowGhost: 'text-primary hover:bg-primary/5',
        button: 'text-primary',
      };
    case 'stringer':
      return {
        accent: 'bg-amber-500',
        chip: 'border-amber-200 bg-amber-50 text-amber-700',
        icon: 'bg-amber-50 text-amber-700',
        now: 'border-amber-200 bg-white',
        nowSoft: 'border-amber-100 bg-amber-50/70',
        nowLabel: 'text-amber-700',
        nowTitle: 'text-text',
        nowText: 'text-text-muted',
        nowBadge: 'border-amber-200 bg-amber-50 text-amber-700',
        nowTrackActive: 'bg-amber-500',
        nowTrackInactive: 'bg-amber-100',
        nowTile: 'border-amber-100 bg-amber-50/70',
        nowGhost: 'text-amber-700 hover:bg-amber-50',
        button: 'text-amber-700',
      };
    case 'academy':
      return {
        accent: 'bg-violet-500',
        chip: 'border-violet-200 bg-violet-50 text-violet-700',
        icon: 'bg-violet-50 text-violet-700',
        now: 'border-violet-200 bg-white',
        nowSoft: 'border-violet-100 bg-violet-50/70',
        nowLabel: 'text-violet-700',
        nowTitle: 'text-text',
        nowText: 'text-text-muted',
        nowBadge: 'border-violet-200 bg-violet-50 text-violet-700',
        nowTrackActive: 'bg-violet-500',
        nowTrackInactive: 'bg-violet-100',
        nowTile: 'border-violet-100 bg-violet-50/70',
        nowGhost: 'text-violet-700 hover:bg-violet-50',
        button: 'text-violet-700',
      };
    case 'workspace':
      return {
        accent: 'bg-zinc-900',
        chip: 'border-slate-300 bg-slate-100 text-slate-800',
        icon: 'bg-slate-100 text-slate-800',
        now: 'border-slate-300 bg-white',
        nowSoft: 'border-slate-200 bg-slate-50',
        nowLabel: 'text-slate-800',
        nowTitle: 'text-text',
        nowText: 'text-text-muted',
        nowBadge: 'border-slate-300 bg-slate-100 text-slate-800',
        nowTrackActive: 'bg-slate-800',
        nowTrackInactive: 'bg-slate-200',
        nowTile: 'border-slate-200 bg-slate-50',
        nowGhost: 'text-slate-800 hover:bg-slate-100',
        button: 'text-slate-800',
      };
    default:
      return {
        accent: 'bg-primary',
        chip: 'border-primary/20 bg-primary/10 text-primary',
        icon: 'bg-primary/10 text-primary',
        now: 'border-primary/20 bg-white',
        nowSoft: 'border-primary/10 bg-primary/5',
        nowLabel: 'text-primary',
        nowTitle: 'text-text',
        nowText: 'text-text-muted',
        nowBadge: 'border-primary/20 bg-primary/10 text-primary',
        nowTrackActive: 'bg-primary',
        nowTrackInactive: 'bg-primary/10',
        nowTile: 'border-primary/10 bg-primary/5',
        nowGhost: 'text-primary hover:bg-primary/5',
        button: 'text-primary',
      };
  }
}

function isProviderWelcomeMode(mode?: WelcomeMode | null): mode is 'COACH' | 'STRINGER' | 'ACADEMY' {
  return mode === 'COACH' || mode === 'STRINGER' || mode === 'ACADEMY';
}

function modeToTone(mode: WelcomeMode): WelcomeTone {
  switch (mode) {
    case 'COACH':
      return 'coach';
    case 'STRINGER':
      return 'stringer';
    case 'ACADEMY':
      return 'academy';
    case 'WORKSPACE':
      return 'workspace';
    default:
      return 'community';
  }
}

function modeToProviderPriority(mode: WelcomeMode, fallback: HomeDashboardPriority): HomeDashboardPriority {
  if (mode === 'COACH' || mode === 'STRINGER' || mode === 'ACADEMY') {
    return mode;
  }
  return fallback;
}

function normalizeLandingMode(value?: string | null): WelcomeMode | null {
  switch ((value || '').trim().toUpperCase()) {
    case 'COMMUNITY':
      return 'COMMUNITY';
    case 'COACH':
      return 'COACH';
    case 'STRINGER':
      return 'STRINGER';
    case 'ACADEMY':
      return 'ACADEMY';
    case 'WORKSPACE':
      return 'WORKSPACE';
    default:
      return null;
  }
}

function firstProviderLandingMode(provider?: HomeDashboardProviderEntry | null): WelcomeMode | null {
  if (!provider?.hasProfile) {
    return null;
  }
  if (provider.providerTypes.includes('COACH')) {
    return 'COACH';
  }
  if (provider.providerTypes.includes('STRINGER')) {
    return 'STRINGER';
  }
  if (provider.providerTypes.includes('ACADEMY')) {
    return 'ACADEMY';
  }
  return null;
}

function getProviderPriorityCopy(
  priority: HomeDashboardPriority,
  provider: HomeDashboardProviderEntry | null,
  linkedWorkspaceName?: string | null,
  linkedWorkspaceLabel?: string | null,
) {
  const providerState = getProviderStateCopy(provider);
  const workspaceLabel = linkedWorkspaceLabel || 'Coach Studio';
  const workspaceName = linkedWorkspaceName || 'your private tools';

  switch (priority) {
    case 'COACH':
      return {
        badge: 'Coach',
        icon: Calendar,
        contextLabel: linkedWorkspaceName || 'Coach',
        title: provider?.workflowState === 'LIVE' ? 'Coach' : 'Coach Profile',
        subtitle:
          provider?.workflowState === 'LIVE'
            ? linkedWorkspaceName || 'Public Profile'
            : formatProviderWorkflowState(provider?.workflowState),
        summary:
          provider?.workflowState === 'LIVE'
            ? linkedWorkspaceName
              ? `${workspaceLabel}: ${workspaceName}`
              : 'Public Profile ready'
            : linkedWorkspaceName
              ? `${workspaceLabel}: ${workspaceName}`
              : providerState.title,
      };
    case 'STRINGER':
      return {
        badge: 'Stringer',
        icon: Wrench,
        contextLabel: linkedWorkspaceName || 'PRO',
        title: 'Stringer dashboard',
        subtitle: workspaceLabel,
        summary: `${providerState.title} • ${workspaceName}`,
      };
    case 'ACADEMY':
      return {
        badge: 'Academy',
        icon: Building2,
        contextLabel: linkedWorkspaceName || 'PRO',
        title: 'Academy dashboard',
        subtitle: workspaceLabel,
        summary: `${providerState.title} • ${workspaceName}`,
      };
    case 'MULTI_PROVIDER':
      return {
        badge: 'PRO',
        icon: Sparkles,
        contextLabel: linkedWorkspaceName || 'PRO',
        title: 'PRO dashboard',
        subtitle: workspaceLabel,
        summary: `${providerState.title} • ${formatProviderTypes(provider?.providerTypes)} on one account`,
      };
    default:
      return {
        badge: 'Welcome back',
        icon: Medal,
        contextLabel: 'TARK',
        title: 'Pick up where you left off.',
        subtitle: 'Open Play, Buzz, or TARK PROs from one place.',
        summary: providerState.title,
      };
  }
}

function getProviderAction(provider?: HomeDashboardProviderEntry | null) {
  if (!provider || !provider.hasProfile) {
    return {
      label: 'Create Public Profile',
      description: 'Create a public coach, stringer, or academy profile.',
      to: '/pros/become',
      icon: Sparkles,
    } satisfies QuickLink;
  }

  switch (provider.workflowState) {
    case 'DRAFT':
      return {
        label: 'Finish profile',
        description: 'Complete the draft so TARK can review your public profile.',
        to: '/pros/manage',
        icon: ClipboardList,
      } satisfies QuickLink;
    case 'IN_REVIEW':
      return {
        label: 'Review profile',
        description: 'Your public profile is in review. Open it to check status and details.',
        to: '/pros/manage',
        icon: ClipboardList,
      } satisfies QuickLink;
    case 'APPROVED':
      return {
        label: 'Publish Public Profile',
        description: 'Your profile is approved. Turn it live when you are ready.',
        to: '/pros/manage',
        icon: Sparkles,
      } satisfies QuickLink;
    default:
      return {
        label: 'Manage Public Profile',
        description: 'Open the public profile attached to this TARK account.',
        to: '/pros/manage',
        icon: Sparkles,
      } satisfies QuickLink;
  }
}

function getProviderStateCopy(provider?: HomeDashboardProviderEntry | null) {
  if (!provider || !provider.hasProfile) {
    return {
      badge: 'Not requested',
      title: 'No public profile yet',
      description:
        'Use Play, Buzz, and TARK PROs now. When you want to coach, string racquets, or run a program, create a public profile from this same account.',
      actionLabel: 'Create Public Profile',
      actionTo: '/pros/become',
    };
  }

  const typeLabel = formatProviderTypes(provider.providerTypes);

  switch (provider.workflowState) {
    case 'DRAFT':
      return {
        badge: 'Draft',
        title: `${typeLabel} request started`,
        description:
          'Profile setup has started, but it is still a draft. Finish the details and send it into TARK review when you are ready.',
        actionLabel: 'Finish profile',
        actionTo: '/pros/manage',
      };
    case 'IN_REVIEW':
      return {
        badge: 'In review',
        title: `${typeLabel} request in TARK review`,
        description:
          'TARK is reviewing public identity, listing content, service area, and contact path before anything appears in PROs.',
        actionLabel: 'Review profile',
        actionTo: '/pros/manage',
      };
    case 'APPROVED':
      return {
        badge: 'Approved',
        title: `${typeLabel} profile approved`,
        description:
          'The public profile passed review. It is ready to go live whenever you decide to publish it.',
        actionLabel: 'Publish Public Profile',
        actionTo: '/pros/manage',
      };
    case 'LIVE':
      return {
        badge: 'Live',
        title: `${typeLabel} profile`,
        description: 'Visible in PROs.',
        actionLabel: 'Manage Public Profile',
        actionTo: '/pros/manage',
      };
    case 'PAUSED':
      return {
        badge: 'Paused',
        title: `${typeLabel} profile is paused`,
        description:
          'The public profile already exists but is currently hidden from public browse until you turn it back on.',
        actionLabel: 'Manage Public Profile',
        actionTo: '/pros/manage',
      };
    case 'SUSPENDED':
      return {
        badge: 'Suspended',
        title: `${typeLabel} profile is suspended`,
        description:
          'The public profile is not available right now. Open it to review the current status and next steps.',
        actionLabel: 'Open Public Profile',
        actionTo: '/pros/manage',
      };
    case 'ARCHIVED':
      return {
        badge: 'Archived',
        title: `${typeLabel} profile is archived`,
        description:
          'The public profile exists on this account but is no longer active in public browse.',
        actionLabel: 'Open Public Profile',
        actionTo: '/pros/manage',
      };
    default:
      return {
        badge: 'Profile',
        title: `${typeLabel} public profile`,
        description: 'This account already has a public profile attached to it.',
        actionLabel: 'Manage Public Profile',
        actionTo: '/pros/manage',
      };
  }
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatRelativeTime(value?: string | null): string {
  if (!value) {
    return 'No recent activity';
  }

  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) {
    return 'No recent activity';
  }

  const diffMinutes = Math.max(1, Math.floor((Date.now() - timestamp) / 60000));
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function resolveDashboardRoute(actionKey: string): string {
  switch (actionKey) {
    case 'schedule':
      return '/schedule';
    case 'directory':
      return '/directory';
    case 'access':
      return '/tenant-access';
    case 'chats':
      return '/squads';
    case 'buzz':
      return '/buzz';
    case 'play':
      return '/play';
    case 'squads':
      return '/squads';
    case 'pros':
      return '/pros';
    case 'profile':
    default:
      return '/profile';
  }
}

function toScopedProviderSummary(profile: ProviderProfile | undefined): HomeDashboardProviderEntry | null {
  if (!profile) return null;
  const publiclyVisible = Boolean(profile.publiclyVisible);
  const workflowState: HomeDashboardProviderEntry['workflowState'] =
    profile.publicationStatus === 'SUBMITTED' ? 'IN_REVIEW'
      : profile.publicationStatus === 'VERIFIED' ? 'APPROVED'
        : profile.publicationStatus === 'PUBLISHED' ? (publiclyVisible ? 'LIVE' : 'PAUSED')
          : profile.publicationStatus ?? 'NONE';
  return {
    hasProfile: true, displayName: profile.displayName, providerTypes: profile.providerTypes,
    workflowState, publicationStatus: profile.publicationStatus, active: Boolean(profile.isActive),
    publiclyVisible, tenantLinked: profile.tenantId != null, tenantId: profile.tenantId,
    workspaceStatus: profile.workspace?.status, workspaceRequestId: profile.workspace?.requestId,
    workspaceDisplayName: profile.workspace?.workspaceDisplayName,
  };
}

export default function WelcomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const {
    memberships,
    activeTenant,
    selectTenant,
    isSwitchingTenant,
    isLoading: isBootstrapLoading,
  } = useTenantBootstrap(Boolean(user));
  const profilePreference = useWebProfile(Boolean(user));
  const updateProfilePreference = useUpdateWebProfile();
  const [selectedWelcomeMode, setSelectedWelcomeMode] = useState<WelcomeMode | null>(null);
  const landingPreferenceAppliedRef = useRef(false);

  const isCommunityModeRequested = isCommunityWelcomeRequest(location.pathname, location.search);
  const requestedWelcomeMode = isCommunityModeRequested ? 'COMMUNITY' : null;
  const isTenantContext = hasTenantWorkspaceContext(activeTenant?.tenant?.id, isCommunityModeRequested);
  const workSurface = (activeTenant?.tenant?.workSurface || '').toLowerCase();
  const workTabLabel = activeTenant?.tenant?.workTabLabel || (isTenantContext ? 'Schedule' : 'Play');
  const directoryTabLabel =
    activeTenant?.tenant?.tenantArchetype === 'COACHING'
      ? 'Students'
      : activeTenant?.tenant?.directoryTabLabel || 'People';
  const activeTenantRole = activeTenant?.roleCode?.toUpperCase() || '';
  const canManageTenant = ['OWNER', 'ADMIN', 'COACH', 'STAFF'].includes(activeTenantRole);
  const isCoachTenantContext = isTenantContext && activeTenant?.tenant?.tenantArchetype === 'COACHING';

  const communityHome = useHomeDashboard(Boolean(user));
  const communitySnapshot = useCommunityDashboardSnapshot(Boolean(user) && !isTenantContext);
  const scheduleDashboard = useScheduleDashboard(Boolean(user) && isTenantContext && workSurface !== 'orders');
  const tenantDirectory = useTenantDirectory(Boolean(user) && isTenantContext);
  const workDashboard = useTenantWorkDashboard(Boolean(user) && isTenantContext && workSurface === 'orders');
  const requestCoachWorkspace = useRequestCoachWorkspace();
  const coachDashboard = scheduleDashboard.data?.coachDashboard ?? null;
  const communityProvider = communityHome.data?.provider ?? null;
  const ownsScopedBusiness = isTenantContext && workSurface !== 'orders' && activeTenantRole === 'OWNER';
  const ownedBusinesses = useMyProsBusinesses(Boolean(user) && ownsScopedBusiness);
  const scopedBusiness = ownsScopedBusiness
    ? ownedBusinesses.data?.find((business) => business.tenantId === activeTenant?.tenant?.id)
    : undefined;
  const scopedProvider = toScopedProviderSummary(scopedBusiness);
  const scopedAnalytics = useMyProsAnalytics(Boolean(scopedBusiness), scopedBusiness?.id);
  const communityPriority = communityHome.data?.dashboardPriority ?? 'COMMUNITY';
  const providerModes = useMemo<WelcomeMode[]>(() => {
    if (!communityProvider?.hasProfile) {
      return [];
    }
    const modes: WelcomeMode[] = [];
    if (communityProvider.providerTypes.includes('COACH')) {
      modes.push('COACH');
    }
    if (communityProvider.providerTypes.includes('STRINGER')) {
      modes.push('STRINGER');
    }
    if (communityProvider.providerTypes.includes('ACADEMY')) {
      modes.push('ACADEMY');
    }
    return modes;
  }, [communityProvider]);
  const availableWelcomeModes = useMemo<WelcomeMode[]>(
    () => Array.from(new Set<WelcomeMode>([
      'COMMUNITY',
      ...providerModes,
      ...(memberships.length > 0 ? ['WORKSPACE' as WelcomeMode] : []),
    ])),
    [memberships.length, providerModes],
  );
  const preferredLandingMode = normalizeLandingMode(profilePreference.data?.preferredLandingMode);
  const defaultWelcomeMode: WelcomeMode = useMemo(() => {
    if (isTenantContext) {
      return 'WORKSPACE';
    }
    if (requestedWelcomeMode) {
      return requestedWelcomeMode;
    }
    if (preferredLandingMode && availableWelcomeModes.includes(preferredLandingMode)) {
      return preferredLandingMode;
    }
    return firstProviderLandingMode(communityProvider) ?? 'COMMUNITY';
  }, [availableWelcomeModes, communityProvider, isTenantContext, preferredLandingMode, requestedWelcomeMode]);
  const activeWelcomeMode =
    selectedWelcomeMode && availableWelcomeModes.includes(selectedWelcomeMode)
      ? selectedWelcomeMode
      : defaultWelcomeMode;
  const providerPriority = modeToProviderPriority(activeWelcomeMode, communityPriority);
  const isProviderLedCommunity =
    !isTenantContext
    && isProviderWelcomeMode(activeWelcomeMode)
    && providerModes.includes(activeWelcomeMode)
    && Boolean(communityProvider?.hasProfile);
  const isCommunityCoachProvider =
    isProviderLedCommunity && activeWelcomeMode === 'COACH';
  const isLiveCommunityCoach =
    isCommunityCoachProvider && communityProvider?.workflowState === 'LIVE';
  const isCommunityOnlyDashboard = !isTenantContext && !isProviderLedCommunity;
  const providerAnalytics = useMyProsAnalytics(
    Boolean(user) && Boolean(communityProvider?.hasProfile) && isProviderLedCommunity,
  );
  const providerAction = useMemo(() => getProviderAction(communityProvider), [communityProvider]);
  const linkedProviderMembership = useMemo(
    () =>
      communityProvider?.tenantId
        ? memberships.find((membership) => membership.tenant?.id === communityProvider.tenantId) ?? null
        : null,
    [communityProvider?.tenantId, memberships],
  );
  const providerWorkspaceStatus =
    communityProvider?.workspaceStatus ||
    (linkedProviderMembership?.tenant ? 'ENABLED' : 'NOT_REQUESTED');

  const visibleCommunitySquads = communitySnapshot.data?.squads.items ?? [];
  const communitySquadCount = communitySnapshot.data?.squads.totalCount ?? 0;
  const nextCoachOccurrence = useMemo(() => {
    if (!isTenantContext || workSurface === 'orders') {
      return null;
    }

    return scheduleDashboard.data?.upcomingOccurrences?.[0] ?? null;
  }, [isTenantContext, scheduleDashboard.data?.upcomingOccurrences, workSurface]);
  const nextCoachOccurrenceId = nextCoachOccurrence?.occurrenceId ?? null;
  const nextSessionDetail = useScheduleOccurrenceDetail(
    nextCoachOccurrenceId,
    Boolean(user) && isTenantContext && workSurface !== 'orders' && nextCoachOccurrenceId != null,
  );
  const nextSessionSlotFillers = useScheduleSlotFillers(
    nextCoachOccurrenceId,
    Boolean(user) && isTenantContext && workSurface !== 'orders' && nextCoachOccurrenceId != null,
  );

  const handleOpenLinkedProviderWorkspace = async () => {
    if (linkedProviderMembership?.tenant?.id) {
      await selectTenant({ tenantId: linkedProviderMembership.tenant.id });
      navigate('/schedule');
      return;
    }

    navigate(providerAction.to === '/pros/manage' || !providerAction.to ? (isTenantContext ? `/pros/manage?tenantId=${activeTenant?.tenant?.id}` : '/pros/businesses') : providerAction.to);
  };

  const handleRequestCoachWorkspace = async () => {
    await requestCoachWorkspace.mutateAsync(undefined);
    await communityHome.refetch();
  };

  useEffect(() => {
    if (landingPreferenceAppliedRef.current || requestedWelcomeMode || profilePreference.isLoading || isBootstrapLoading) {
      return;
    }
    if (!preferredLandingMode || !availableWelcomeModes.includes(preferredLandingMode)) {
      landingPreferenceAppliedRef.current = true;
      return;
    }

    landingPreferenceAppliedRef.current = true;
    setSelectedWelcomeMode(preferredLandingMode);

    if (preferredLandingMode === 'WORKSPACE') {
      if (!isTenantContext) {
        const targetMembership = linkedProviderMembership ?? memberships[0] ?? null;
        if (targetMembership?.tenant?.id) {
          void selectTenant({ tenantId: targetMembership.tenant.id });
        }
      }
      return;
    }

    if (isTenantContext) {
      void selectTenant({ communityMode: true });
    }
  }, [
    availableWelcomeModes,
    isBootstrapLoading,
    isTenantContext,
    linkedProviderMembership,
    memberships,
    preferredLandingMode,
    profilePreference.isLoading,
    requestedWelcomeMode,
    selectTenant,
  ]);

  const persistLandingMode = (mode: WelcomeMode) => {
    void updateProfilePreference.mutateAsync({ preferredLandingMode: mode }).catch(() => undefined);
  };

  const handleSelectWelcomeMode = async (mode: WelcomeMode) => {
    setSelectedWelcomeMode(mode);
    persistLandingMode(mode);

    if (mode === 'WORKSPACE') {
      const targetMembership = linkedProviderMembership ?? memberships[0] ?? null;
      if (targetMembership?.tenant?.id) {
        await selectTenant({ tenantId: targetMembership.tenant.id });
      }
      navigate('/welcome');
      return;
    }

    if (isTenantContext) {
      await selectTenant({ communityMode: true });
    }
    navigate('/welcome');
  };

  const modeOptions = useMemo<WelcomeModeOption[]>(() => {
    const workspaceModeLabel =
      isCoachTenantContext || linkedProviderMembership?.tenant?.tenantArchetype === 'COACHING'
        ? 'Coach Studio'
        : 'Workspace';
    const labels: Record<WelcomeMode, string> = {
      COMMUNITY: 'Play',
      COACH: 'Coach',
      STRINGER: 'Stringer',
      ACADEMY: 'Academy',
      WORKSPACE: workspaceModeLabel,
    };

    return availableWelcomeModes.map((mode) => ({
      mode,
      label: labels[mode],
      tone: modeToTone(mode),
      active: mode === activeWelcomeMode,
      disabled: mode === 'WORKSPACE' && memberships.length === 0,
      onSelect: () => {
        void handleSelectWelcomeMode(mode);
      },
    }));
  }, [
    activeWelcomeMode,
    availableWelcomeModes,
    isCoachTenantContext,
    linkedProviderMembership?.tenant?.tenantArchetype,
    memberships.length,
  ]);

  const quickLinks = useMemo<QuickLink[]>(() => {
    if (!isTenantContext) {
      if (isProviderLedCommunity) {
        const providerWorkspaceLabel =
          linkedProviderMembership?.tenant?.workTabLabel ||
          (providerPriority === 'STRINGER' ? 'Work' : 'Schedule');
        const providerWorkspaceIcon =
          providerPriority === 'STRINGER'
            ? Wrench
            : providerPriority === 'ACADEMY'
              ? Building2
              : Calendar;

        if (isCommunityCoachProvider) {
          return [
            {
              label: 'Public Profile',
              description: formatProviderWorkflowState(communityProvider?.workflowState),
              to: '/pros/manage',
              icon: ClipboardList,
            },
            linkedProviderMembership?.tenant
              ? {
                  label: providerWorkspaceLabel,
                  description: linkedProviderMembership.tenant.displayName,
                  onClick: () => {
                    void handleOpenLinkedProviderWorkspace();
                  },
                  icon: providerWorkspaceIcon,
                }
              : {
                  label: 'Profile',
                  description: 'Account',
                  to: '/profile',
                  icon: ShieldCheck,
                },
          ];
        }

        return [
          linkedProviderMembership?.tenant
            ? {
                label: providerWorkspaceLabel,
                description: linkedProviderMembership.tenant.displayName,
                onClick: () => {
                  void handleOpenLinkedProviderWorkspace();
                },
                icon: providerWorkspaceIcon,
              }
            : { ...providerAction, description: 'PRO setup' },
          {
            label: 'Public Profile',
            description: 'Status and visibility',
            to: '/pros/manage',
            icon: ClipboardList,
          },
          {
            label: 'Squads',
            description: 'Play',
            to: '/squads',
            icon: Users,
          },
          {
            label: 'Play',
            description: 'Play history',
            to: '/play',
            icon: Medal,
          },
        ];
      }

      return [
        {
          label: 'Open Squads',
          description: 'Groups, DMs, and the latest squad activity.',
          to: '/squads',
          icon: Users,
        },
        {
          label: 'Open Play',
          description: 'Scorecards, match history, and tracked results.',
          to: '/play',
          icon: Medal,
        },
        {
          label: 'Open Buzz',
          description: 'Videos, scores, and public sports storylines.',
          to: '/buzz',
          icon: Radio,
        },
        {
          label: 'Browse PROs',
          description: 'Find coaches, academies, and stringers near you.',
          to: '/pros',
          icon: Sparkles,
        },
        {
          label: 'Open Me',
          description: 'Profile, privacy, and account settings.',
          to: '/profile',
          icon: ShieldCheck,
        },
      ];
    }

    return [
      {
        label: workTabLabel,
        description: workSurface === 'orders' ? 'Queue' : 'Upcoming sessions',
        to: '/schedule',
        icon: workSurface === 'orders' ? ClipboardList : Calendar,
      },
      {
        label: directoryTabLabel,
        description: 'Students and parents',
        to: '/directory',
        icon: Users,
      },
      {
        label: canManageTenant ? 'Access' : 'Family',
        description: canManageTenant
          ? 'Codes and invites'
          : 'Household athletes',
        to: canManageTenant ? '/tenant-access' : '/family',
        icon: canManageTenant ? KeyRound : UserPlus,
      },
      {
        label: 'Me',
        description: 'Profile settings',
        to: '/profile',
        icon: ShieldCheck,
      },
    ];
  }, [
    providerPriority,
    canManageTenant,
    directoryTabLabel,
    handleOpenLinkedProviderWorkspace,
    isCommunityCoachProvider,
    isProviderLedCommunity,
    isTenantContext,
    linkedProviderMembership,
    providerAction,
    workSurface,
    workTabLabel,
  ]);

  const hero = useMemo(() => {
    if (!isTenantContext) {
      const providerState = getProviderStateCopy(communityProvider);
      const isCommunityOnly = !communityProvider?.hasProfile;
      if (isProviderLedCommunity) {
        return getProviderPriorityCopy(
          providerPriority,
          communityProvider,
          linkedProviderMembership?.tenant?.displayName,
          linkedProviderMembership?.tenant?.workTabLabel,
        );
      }

      return {
        badge: 'Welcome back',
        icon: Medal,
        contextLabel: 'TARK',
        title: isCommunityOnly
          ? `Welcome back${communityHome.data?.greetingName ? `, ${communityHome.data.greetingName}` : ''}`
          : communityHome.data?.headline || 'Welcome back.',
        subtitle: isCommunityOnly
          ? 'Open Play, catch up on Buzz, or browse TARK PROs.'
          : communityHome.data?.subtitle ||
            'The usual TARK surfaces stay available even when this account also has profile progress or private tools attached.',
        summary: isCommunityOnly
          ? memberships.length > 0
            ? `${memberships.length} organization${memberships.length === 1 ? '' : 's'} available when you need them.`
            : 'Start with the surface you use most.'
          : providerState.title,
      };
    }

    if (workSurface === 'orders') {
      return {
        badge: isCoachTenantContext ? 'Coach Studio' : 'Workspace welcome',
        icon: Wrench,
        contextLabel: activeTenant?.tenant?.displayName || 'Private workspace',
        title: workDashboard.data?.headline || `Run ${workTabLabel.toLowerCase()} from one calm workspace.`,
        subtitle:
          workDashboard.data?.subtitle ||
          `Keep ${workTabLabel.toLowerCase()}, ${directoryTabLabel.toLowerCase()}, and access management connected without pushing provider work into the public marketplace.`,
        summary: `${formatRole(activeTenant?.roleCode)} • ${workTabLabel}`,
      };
    }

    return {
      badge: isCoachTenantContext ? 'Coach Studio' : 'Workspace welcome',
      icon: Calendar,
      contextLabel: activeTenant?.tenant?.displayName || (isCoachTenantContext ? 'Coach Studio' : 'Private workspace'),
      title: scheduleDashboard.data?.headline || `Welcome back to ${workTabLabel}.`,
      subtitle:
        coachDashboard?.subtitle ||
        (isCoachTenantContext
          ? 'Keep the coaching day simple: schedule, students, access, and progress stay in one studio.'
          : 'Keep the coaching day simple: schedule, people, access, and program health stay in one workspace.'),
      summary: `${formatRole(activeTenant?.roleCode)} • ${workTabLabel}`,
    };
  }, [
    activeTenant?.roleCode,
    activeTenant?.tenant?.displayName,
    providerPriority,
    communityProvider,
    communityHome.data?.headline,
    communityHome.data?.subtitle,
    directoryTabLabel,
    isProviderLedCommunity,
    isTenantContext,
    linkedProviderMembership?.tenant?.displayName,
    linkedProviderMembership?.tenant?.workTabLabel,
    isCoachTenantContext,
    memberships.length,
    coachDashboard?.subtitle,
    scheduleDashboard.data?.headline,
    workDashboard.data?.headline,
    workDashboard.data?.subtitle,
    workSurface,
    workTabLabel,
  ]);

  const metrics = useMemo<Metric[]>(() => {
    if (!isTenantContext) {
      if (isCommunityOnlyDashboard) {
        return [];
      }

      if (isProviderLedCommunity) {
        if (isCommunityCoachProvider) {
          return [];
        }

        const providerMetrics: Metric[] = [
          {
            label: 'Profile roles',
            value: String(communityProvider?.providerTypes.length || 0),
            description: 'Roles currently attached to this same TARK account.',
          },
          {
            label: 'Public Profile',
            value: formatProviderWorkflowState(communityProvider?.workflowState),
            description: linkedProviderMembership?.tenant
              ? `Linked to ${linkedProviderMembership.tenant.displayName}.`
              : 'Public profile status for this account.',
          },
        ];

        if (
          communityProvider?.publiclyVisible &&
          providerAnalytics.data &&
          (providerAnalytics.data.profileViews > 0 || providerAnalytics.data.contactClicks > 0)
        ) {
          providerMetrics.push({
            label: 'Views',
            value: String(providerAnalytics.data.profileViews),
            description: `${providerAnalytics.data.contactClicks} contact actions recorded so far.`,
          });
        }

        providerMetrics.push(
          {
            label: 'Play',
            value: String(communitySquadCount),
            description: 'Play mode stays available on the same account.',
          },
        );

        return providerMetrics;
      }

      if (communityHome.data?.stats?.length) {
        return communityHome.data.stats.slice(0, 3).map((item) => ({
          label: item.label,
          value: item.value,
          description: item.description,
        }));
      }

      return [
        {
          label: 'Squads',
          value: String(visibleCommunitySquads.length),
          description: 'Play groups visible on this account.',
        },
        {
          label: 'Studios',
          value: String(memberships.length),
          description: 'Private tools available to switch into.',
        },
        {
          label: 'Mode',
          value: 'Play',
          description: 'Open play, squads, and discovery are active.',
        },
      ];
    }

    if (workSurface === 'orders') {
      if (workDashboard.data?.stats?.length) {
        const workMetrics = workDashboard.data.stats.slice(0, 2).map((item) => ({
          label: item.label,
          value: item.value,
          description: item.description,
        }));

        return [
          ...workMetrics,
          {
            label: directoryTabLabel,
            value: String(tenantDirectory.directory?.access?.totalDirectoryCount || 0),
            description: `Visible ${directoryTabLabel.toLowerCase()} linked to this workspace.`,
          },
          {
            label: 'Invites',
            value: String(tenantDirectory.directory?.access?.pendingInvitationCount || 0),
            description: 'Pending access invites.',
          },
        ];
      }

      return [
        {
          label: 'Requests',
          value: String(workDashboard.data?.recentRequests.length || 0),
          description: 'Recent provider or seller requests in the current queue.',
        },
        {
          label: directoryTabLabel,
          value: String(tenantDirectory.directory?.access?.totalDirectoryCount || 0),
          description: `Visible ${directoryTabLabel.toLowerCase()} linked to this workspace.`,
        },
        {
          label: 'Invites',
          value: String(tenantDirectory.directory?.access?.pendingInvitationCount || 0),
          description: 'Pending access invites.',
        },
        {
          label: 'Status',
          value: workTabLabel,
          description: 'Current work surface for this tenant archetype.',
        },
      ];
    }

    if (coachDashboard?.metrics?.length) {
      const coachMetrics = coachDashboard.metrics.slice(0, 4).map((item) => ({
        label: item.label,
        value: item.value,
        description: item.description,
      }));

      return [
        ...coachMetrics,
        {
          label: directoryTabLabel,
          value: String(tenantDirectory.directory?.access?.totalDirectoryCount || 0),
          description: 'People in this workspace.',
        },
        {
          label: 'Invites',
          value: String(tenantDirectory.directory?.access?.pendingInvitationCount || 0),
          description: 'Pending access invites.',
        },
      ];
    }

    if (scheduleDashboard.data?.stats?.length) {
      const scheduleMetrics = scheduleDashboard.data.stats.slice(0, 4).map((item) => ({
        label: item.label,
        value: item.value,
        description: item.description,
      }));

      return [
        ...scheduleMetrics,
        {
          label: directoryTabLabel,
          value: String(tenantDirectory.directory?.access?.totalDirectoryCount || 0),
          description: 'People in this workspace.',
        },
        {
          label: 'Invites',
          value: String(tenantDirectory.directory?.access?.pendingInvitationCount || 0),
          description: 'Pending access invites.',
        },
      ];
    }

    return [
      {
        label: 'Upcoming',
        value: String(scheduleDashboard.data?.upcomingOccurrences.length || 0),
        description: 'Visible sessions on the current coaching board.',
      },
      {
        label: 'Programs',
        value: String(scheduleDashboard.data?.programs.length || 0),
        description: 'Programs feeding the active schedule.',
      },
      {
        label: directoryTabLabel,
        value: String(tenantDirectory.directory?.access?.totalDirectoryCount || 0),
        description: `Visible ${directoryTabLabel.toLowerCase()} in this workspace.`,
      },
      {
        label: 'Invites',
        value: String(tenantDirectory.directory?.access?.pendingInvitationCount || 0),
        description: 'Pending access invites.',
      },
    ];
  }, [
    communityProvider?.providerTypes.length,
    communityProvider?.publiclyVisible,
    communityProvider?.workflowState,
    providerPriority,
    communitySquadCount,
    communityHome.data?.stats,
    directoryTabLabel,
    isCommunityOnlyDashboard,
    isCommunityCoachProvider,
    isProviderLedCommunity,
    isTenantContext,
    linkedProviderMembership?.tenant,
    memberships.length,
    coachDashboard?.metrics,
    providerAnalytics.data,
    scheduleDashboard.data?.programs.length,
    scheduleDashboard.data?.stats,
    scheduleDashboard.data?.upcomingOccurrences.length,
    tenantDirectory.directory?.access?.totalDirectoryCount,
    tenantDirectory.directory?.access?.pendingInvitationCount,
    workDashboard.data?.recentRequests.length,
    workDashboard.data?.stats,
    workSurface,
    workTabLabel,
  ]);

  const metricGridClassName = useMemo(() => {
    if (metrics.length >= 6) {
      return 'grid gap-3 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6';
    }
    if (metrics.length >= 4) {
      return 'grid gap-3 grid-cols-2 lg:grid-cols-4';
    }
    return 'grid gap-3 grid-cols-2 lg:grid-cols-3';
  }, [metrics.length]);
  const headerEyebrow = isLiveCommunityCoach
    ? 'TARK PRO'
    : isTenantContext
      ? isCoachTenantContext
        ? 'Coach Studio'
        : activeTenant?.tenant?.displayName || 'Workspace'
      : hero.contextLabel;
  const headerTitle = isTenantContext
    ? workSurface === 'orders'
      ? `${workTabLabel} command center`
      : activeTenant?.tenant?.displayName || 'Coach Studio'
    : isCommunityCoachProvider
      ? hero.title
    : isProviderLedCommunity
      ? `${formatProviderTypes(communityProvider?.providerTypes)} command center`
      : hero.title;
  const headerChips = (
    isTenantContext
      ? [
          formatRole(activeTenant?.roleCode),
          isCoachTenantContext ? 'Coach Studio' : workTabLabel,
          directoryTabLabel,
          communityProvider?.hasProfile ? `Profile ${formatProviderWorkflowState(communityProvider.workflowState)}` : null,
        ]
      : isCommunityCoachProvider
        ? isLiveCommunityCoach
          ? ['Live']
          : [
              formatProviderWorkflowState(communityProvider?.workflowState),
              communityProvider?.publiclyVisible ? 'Live' : 'Private',
              linkedProviderMembership?.tenant?.workTabLabel ?? null,
            ]
      : isProviderLedCommunity
        ? [
            formatProviderTypes(communityProvider?.providerTypes),
            formatProviderWorkflowState(communityProvider?.workflowState),
            linkedProviderMembership?.tenant?.workTabLabel ?? null,
          ]
        : [hero.badge]
  ).filter((chip): chip is string => Boolean(chip));
  const welcomeTone: WelcomeTone = isTenantContext
    ? 'workspace'
    : isProviderLedCommunity
      ? getProviderTone(communityProvider, providerPriority)
      : 'community';
  const headerSubtitle = isTenantContext
    ? activeTenant?.tenant?.displayName || hero.summary
    : isLiveCommunityCoach
      ? linkedProviderMembership?.tenant?.displayName || 'Public Profile ready'
      : isProviderLedCommunity
        ? hero.summary
        : 'Squads, Play, Buzz, and PROs';
  const welcomeActions = (isLiveCommunityCoach ? [] : quickLinks).map((link) => ({
    ...link,
    label: link.label.replace(/^Open\s+/i, ''),
  })) satisfies WelcomeAction[];

  const isLoading =
    isBootstrapLoading ||
    (ownsScopedBusiness && (ownedBusinesses.isLoading || (Boolean(scopedBusiness) && scopedAnalytics.isLoading))) ||
    profilePreference.isLoading ||
    (!isTenantContext && (communityHome.isLoading || communitySnapshot.isLoading || (isProviderLedCommunity && providerAnalytics.isLoading))) ||
    (isTenantContext &&
      (workSurface === 'orders'
        ? workDashboard.isLoading
        : scheduleDashboard.isLoading));

  const hasPrimaryError = (ownsScopedBusiness && (ownedBusinesses.isError || (Boolean(scopedBusiness) && scopedAnalytics.isError))) || (!isTenantContext
    ? communityHome.isError || communitySnapshot.isError || (isProviderLedCommunity && providerAnalytics.isError)
    : workSurface === 'orders'
      ? workDashboard.isError
      : scheduleDashboard.isError);

  const handleRetry = async () => {
    if (!isTenantContext) {
      await Promise.all([
        communityHome.refetch(),
        communitySnapshot.refetch(),
        isProviderLedCommunity ? providerAnalytics.refetch() : Promise.resolve(),
      ]);
      return;
    }

    if (workSurface === 'orders') {
      await workDashboard.refetch();
      return;
    }

    await Promise.all([
      scheduleDashboard.refetch(),
      ownsScopedBusiness ? ownedBusinesses.refetch() : Promise.resolve(),
      scopedBusiness ? scopedAnalytics.refetch() : Promise.resolve(),
    ]);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (hasPrimaryError) {
    return (
      <Card variant="default" className="max-w-xl">
        <CardHeader>
          <CardTitle>Welcome page unavailable</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-text-muted">
            We could not load the current view right now. Try again and we’ll restore the landing page for this account.
          </p>
          <Button onClick={handleRetry}>Retry</Button>
        </CardContent>
      </Card>
    );
  }

  if (isCommunityOnlyDashboard && communitySnapshot.data) {
    return (
      <CommunityWelcomeDashboard
        snapshot={communitySnapshot.data}
        membershipsCount={memberships.length}
        modeOptions={modeOptions}
        onOpenSquad={(squadId) => navigate(`/squads/${squadId}`)}
        onOpenSquads={() => navigate('/squads')}
        onOpenPlay={() => navigate('/play')}
        onOpenBuzz={() => navigate('/buzz')}
        onOpenPros={() => navigate('/pros')}
        onBecomePro={() => navigate('/pros/become')}
        onJoinOrg={() => navigate('/join-organization')}
      />
    );
  }

  const handleWelcomeAction = (link: QuickLink) => {
    if (link.onClick) {
      link.onClick();
      return;
    }
    navigate(link.to || '/welcome');
  };

  if (isTenantContext && workSurface !== 'orders') {
    return (
      <CoachStudioLanding
        workspaceName={activeTenant?.tenant?.displayName ?? null}
        roleLabel={formatRole(activeTenant?.roleCode)}
        modeOptions={modeOptions}
        workTabLabel={workTabLabel}
        directoryLabel={directoryTabLabel}
        accessLabel={canManageTenant ? 'Access' : 'Family'}
        occurrences={scheduleDashboard.data?.upcomingOccurrences || []}
        programCount={scheduleDashboard.data?.programs.length ?? 0}
        coachDashboard={coachDashboard}
        nextSessionDetail={nextSessionDetail.data ?? null}
        nextSessionSlotFillers={nextSessionSlotFillers.data ?? null}
        isNextSessionLoading={nextSessionDetail.isLoading || nextSessionSlotFillers.isLoading}
        activeCount={tenantDirectory.directory?.access?.activeDirectoryCount ?? 0}
        linkedAthleteCount={tenantDirectory.directory?.access?.linkedAthleteCount ?? 0}
        pendingInvitationCount={tenantDirectory.directory?.access?.pendingInvitationCount ?? 0}
        activeJoinCodeCount={tenantDirectory.directory?.access?.activeJoinCodeCount ?? 0}
        provider={scopedProvider}
        providerViews={scopedAnalytics.data?.profileViews ?? 0}
        providerContacts={scopedAnalytics.data?.contactClicks ?? 0}
        onOpenCatalog={() => navigate('/classes')}
        onOpenWork={() => navigate('/schedule#upcoming-sessions')}
        onOpenDirectory={() => navigate('/directory#students')}
        onOpenAccess={() => navigate(canManageTenant ? '/tenant-access#join-codes' : '/family')}
        onManagePros={() => navigate(isTenantContext ? `/pros/manage?tenantId=${activeTenant?.tenant?.id}` : '/pros/businesses')}
        onNavigate={(actionKey) => navigate(resolveDashboardRoute(actionKey))}
      />
    );
  }

  return (
    <WelcomeLandingShell
      eyebrow={headerEyebrow}
      title={headerTitle}
      subtitle={headerSubtitle}
      chips={headerChips}
      tone={welcomeTone}
      quickLinks={welcomeActions}
      metrics={metrics}
      metricGridClassName={metricGridClassName}
      modeOptions={modeOptions}
      compact={isLiveCommunityCoach}
      onNavigate={handleWelcomeAction}
    >
      <div className={isLiveCommunityCoach ? 'grid gap-5' : 'grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]'}>
          {!isTenantContext ? (
            isProviderLedCommunity ? (
              <ProviderPrimaryPanel
                priority={providerPriority}
                provider={communityProvider}
                providerViews={providerAnalytics.data?.profileViews ?? 0}
                providerContacts={providerAnalytics.data?.contactClicks ?? 0}
                linkedWorkspaceName={linkedProviderMembership?.tenant?.displayName ?? null}
                linkedWorkspaceLabel={linkedProviderMembership?.tenant?.workTabLabel ?? null}
                workspaceStatus={providerWorkspaceStatus}
                isSwitchingWorkspace={isSwitchingTenant}
                isRequestingWorkspace={requestCoachWorkspace.isPending}
                onOpenWorkspace={() => {
                  void handleOpenLinkedProviderWorkspace();
                }}
                onRequestWorkspace={() => {
                  void handleRequestCoachWorkspace();
                }}
                onManagePros={() => navigate(isTenantContext ? `/pros/manage?tenantId=${activeTenant?.tenant?.id}` : '/pros/businesses')}
                onOpenCommunityBrowse={() => navigate('/pros')}
              />
            ) : (
              <CommunityFocusPanel
                squads={visibleCommunitySquads}
                onOpenSquad={(squadId) => navigate(`/squads/${squadId}`)}
                onOpenSquads={() => navigate('/squads')}
                onOpenPlay={() => navigate('/play')}
              />
            )
          ) : (
            <OrdersFocusPanel
              workTabLabel={workTabLabel}
              directoryLabel={directoryTabLabel}
              requests={workDashboard.data?.recentRequests || []}
              onOpenWork={() => navigate('/schedule')}
              onOpenDirectory={() => navigate('/directory')}
            />
          )}

          {isLiveCommunityCoach ? null : (
            <div className="space-y-5">
              {!isTenantContext ? (
                isProviderLedCommunity ? (
                  <CommunitySecondaryPanel
                    memberships={memberships}
                    squadCount={communitySquadCount}
                    onOpenSquads={() => navigate('/squads')}
                    onOpenPlay={() => navigate('/play')}
                    onJoinOrg={() => navigate('/join-organization')}
                    onOpenProfile={() => navigate('/profile')}
                  />
                ) : (
                  <CommunityGrowthPanel
                    memberships={memberships}
                    onOpenBuzz={() => navigate('/buzz')}
                    onOpenPros={() => navigate('/pros')}
                    onJoinOrg={() => navigate('/join-organization')}
                    onBecomePro={() => navigate('/pros/become')}
                    onOpenProfile={() => navigate('/profile')}
                  />
                )
              ) : (
                <ProviderWorkspacePanel
                  workTabLabel={workTabLabel}
                  directoryLabel={directoryTabLabel}
                  provider={workDashboard.data?.linkedProvider ?? null}
                  directoryCount={tenantDirectory.directory?.access?.totalDirectoryCount ?? 0}
                  pendingInvitationCount={tenantDirectory.directory?.access?.pendingInvitationCount ?? 0}
                  onOpenDirectory={() => navigate('/directory')}
                  onOpenProfile={() => navigate('/profile')}
                />
              )}

              {!isTenantContext && !isCommunityOnlyDashboard ? (
                <WorkspaceOverviewPanel
                  memberships={memberships}
                  activeTenantId={activeTenant?.tenant?.id ?? null}
                  onOpenProfile={() => navigate('/profile')}
                />
              ) : null}
            </div>
          )}
      </div>
    </WelcomeLandingShell>
  );
}

function WelcomeLandingShell({
  eyebrow,
  title,
  subtitle,
  chips,
  tone,
  quickLinks,
  metrics,
  metricGridClassName,
  modeOptions,
  compact = false,
  fullWidth = false,
  onNavigate,
  children,
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
  chips: string[];
  tone: WelcomeTone;
  quickLinks: WelcomeAction[];
  metrics: Metric[];
  metricGridClassName: string;
  modeOptions: WelcomeModeOption[];
  compact?: boolean;
  fullWidth?: boolean;
  onNavigate: (link: QuickLink) => void;
  children: ReactNode;
}) {
  const toneClasses = getWelcomeToneClasses(tone);
  const primaryActions = compact ? [] : quickLinks.slice(0, 2);
  const showStatusRail = !compact && (metrics.length > 0 || chips.length > 0);
  const showQuickLinks = !compact && quickLinks.length > 0;
  const showMetricGrid = !compact && metrics.length > 0;

  return (
    <AppPageFrame width={fullWidth ? 'full' : 'bounded'} className="space-y-5">
      <section className="overflow-hidden rounded-lg border border-border bg-white shadow-[var(--shadow-card)]">
        <div className={`h-1.5 ${toneClasses.accent}`} />
        <div className="space-y-4 p-4 md:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">{eyebrow}</p>
              <p className="mt-1 truncate text-sm font-semibold text-text">{subtitle}</p>
            </div>

            <WelcomeModeSwitch options={modeOptions} />
          </div>

          <div className={showStatusRail ? 'grid gap-4 lg:grid-cols-[minmax(0,1.55fr)_minmax(280px,0.75fr)]' : 'grid gap-4'}>
            <div className={`${toneClasses.now} rounded-lg border p-5 md:p-6`}>
              <div className="flex flex-wrap gap-2">
                {chips.map((chip) => (
                  <span
                    key={chip}
                    className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${toneClasses.chip}`}
                  >
                    {chip}
                  </span>
                ))}
              </div>
              {compact ? null : (
                <p className={`mt-5 text-[11px] font-semibold uppercase tracking-[0.18em] ${toneClasses.nowLabel}`}>Now</p>
              )}
              <h1 className={`${compact ? 'mt-3' : 'mt-2'} text-2xl font-semibold tracking-tight sm:text-3xl ${toneClasses.nowTitle}`}>{title}</h1>
              {compact ? null : (
                <p className={`mt-2 max-w-2xl text-sm font-medium ${toneClasses.nowText}`}>{subtitle}</p>
              )}
              {primaryActions.length > 0 ? (
                <div className="mt-5 flex flex-wrap gap-3">
                  {primaryActions.map((link, index) => (
                    <Button
                      key={`${link.label}-${link.to || link.description}-hero`}
                      variant={index === 0 ? 'primary' : 'outline'}
                      onClick={() => onNavigate(link)}
                      rightIcon={<ArrowRight className="h-4 w-4" />}
                    >
                      {link.label}
                    </Button>
                  ))}
                </div>
              ) : null}
            </div>

            {showStatusRail ? (
            <div className="rounded-lg border border-border bg-surface p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Status</p>
                <span className={`h-2.5 w-2.5 rounded-full ${toneClasses.accent}`} />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {metrics.slice(0, 4).map((metric) => (
                  <div key={`${metric.label}-rail`} className="rounded-lg border border-border bg-white px-3 py-3">
                    <p className="truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-text-subtle">
                      {metric.label}
                    </p>
                    <p className="mt-1.5 truncate text-lg font-semibold text-text">{metric.value}</p>
                  </div>
                ))}
                {metrics.length === 0
                  ? chips.slice(0, 4).map((chip) => (
                      <div key={`${chip}-rail`} className="rounded-lg border border-border bg-white px-3 py-3">
                        <p className="truncate text-sm font-semibold text-text">{chip}</p>
                      </div>
                    ))
                  : null}
              </div>
            </div>
            ) : null}
          </div>

          {showQuickLinks ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
            {quickLinks.map((link) => (
              <button
                key={`${link.label}-${link.to || link.description}`}
                type="button"
                onClick={() => onNavigate(link)}
                className="group flex min-h-[88px] items-center gap-3 rounded-lg border border-border bg-surface-elevated/30 px-3.5 py-3 text-left transition-colors hover:border-primary/30 hover:bg-white"
              >
                <div className={`rounded-lg p-2.5 transition-colors group-hover:bg-white ${toneClasses.icon}`}>
                  <link.icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-text">{link.label}</p>
                  <p className="mt-0.5 truncate text-xs font-medium text-text-subtle">
                    {link.value ?? link.description}
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 shrink-0 text-text-subtle transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
              </button>
            ))}
          </div>
          ) : null}

          {showMetricGrid ? (
            <div className={metricGridClassName}>
              {metrics.map((metric) => (
                <MetricCard key={metric.label} metric={metric} compact />
              ))}
            </div>
          ) : null}
        </div>
      </section>

      {children}
    </AppPageFrame>
  );
}

function WelcomeModeSwitch({ options }: { options: WelcomeModeOption[] }) {
  if (options.length <= 1) {
    return null;
  }

  return (
    <div className="flex min-w-0 flex-wrap gap-2 rounded-lg border border-border bg-surface p-1">
      {options.map((option) => {
        const toneClasses = getWelcomeToneClasses(option.tone);
        return (
          <button
            key={option.mode}
            type="button"
            onClick={option.onSelect}
            disabled={option.disabled}
            className={`rounded-md border px-3 py-2 text-xs font-semibold transition-colors ${
              option.active
                ? toneClasses.chip
                : 'border-transparent bg-transparent text-text-muted hover:border-border hover:bg-white hover:text-text'
            } ${option.disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function MetricCard({ metric, compact = false }: { metric: Metric; compact?: boolean }) {
  return (
    <Card variant="default" padding="none" className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">{metric.label}</p>
            <p className="mt-2 truncate text-3xl font-semibold tracking-tight text-text">{metric.value}</p>
          </div>
          <span className="mt-1 h-2.5 w-2.5 rounded-full bg-primary/70" />
        </div>
        {!compact && metric.description ? (
          <p className="mt-3 text-sm leading-6 text-text-muted">{metric.description}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function CommunityWelcomeDashboard({
  snapshot,
  membershipsCount,
  modeOptions,
  onOpenSquad,
  onOpenSquads,
  onOpenPlay,
  onOpenBuzz,
  onOpenPros,
  onBecomePro,
  onJoinOrg,
}: {
  snapshot: CommunityDashboardSnapshotResponse;
  membershipsCount: number;
  modeOptions: WelcomeModeOption[];
  onOpenSquad: (squadId: number) => void;
  onOpenSquads: () => void;
  onOpenPlay: () => void;
  onOpenBuzz: () => void;
  onOpenPros: () => void;
  onBecomePro: () => void;
  onJoinOrg: () => void;
}) {
  const communityActions: WelcomeAction[] = [
    {
      label: 'Squads',
      description: 'Groups and chats',
      icon: Users,
      onClick: onOpenSquads,
    },
    {
      label: 'Matches',
      description: 'Scores and history',
      icon: Medal,
      onClick: onOpenPlay,
    },
    {
      label: 'Buzz',
      description: 'Today',
      icon: Radio,
      onClick: onOpenBuzz,
    },
    {
      label: 'PROs',
      description: 'Browse',
      icon: Sparkles,
      onClick: onOpenPros,
    },
  ];

  return (
    <WelcomeLandingShell
      eyebrow="TARK"
      title={`Hi, ${snapshot.greetingName}`}
      subtitle="Squads, Matches, Buzz, and PROs"
      chips={[]}
      tone="community"
      quickLinks={communityActions}
      metrics={[]}
      metricGridClassName="grid gap-3 grid-cols-2 lg:grid-cols-4"
      modeOptions={modeOptions}
      fullWidth
      onNavigate={(link) => {
        if (link.onClick) {
          link.onClick();
        }
      }}
    >
      <CommunityNowPanel
        snapshot={snapshot}
        onOpenSquad={onOpenSquad}
        onOpenSquads={onOpenSquads}
        onOpenPlay={onOpenPlay}
      />

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_390px] 2xl:grid-cols-[minmax(0,1fr)_430px]">
        <main className="min-w-0 space-y-6">
          <CommunitySquadsPanel
            snapshot={snapshot}
            onOpenSquad={onOpenSquad}
            onOpenSquads={onOpenSquads}
          />
          <CommunityPlayPanel play={snapshot.play} onOpenPlay={onOpenPlay} />
        </main>

        <aside className="min-w-0 space-y-6 xl:sticky xl:top-8 xl:self-start">
          <CommunityBuzzRail onOpenBuzz={onOpenBuzz} />
          <CommunitySecondaryActions
            membershipsCount={Math.max(membershipsCount, snapshot.membershipsCount)}
            onOpenPros={onOpenPros}
            onBecomePro={onBecomePro}
            onJoinOrg={onJoinOrg}
          />
        </aside>
      </div>
    </WelcomeLandingShell>
  );
}

function CommunityNowPanel({
  snapshot,
  onOpenSquad,
  onOpenSquads,
  onOpenPlay,
}: {
  snapshot: CommunityDashboardSnapshotResponse;
  onOpenSquad: (squadId: number) => void;
  onOpenSquads: () => void;
  onOpenPlay: () => void;
}) {
  const highlightedSquad =
    snapshot.squads.items.find((squad) => (squad.unreadCount || 0) > 0) ?? snapshot.squads.items[0] ?? null;
  const hasUnread = (highlightedSquad?.unreadCount || 0) > 0;
  const toneClasses = getWelcomeToneClasses('community');

  return (
    <Card variant="default" padding="none" className="overflow-hidden border-sky-200">
      <CardContent className={`${toneClasses.now} p-5 md:p-6`}>
        <div>
          <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${toneClasses.nowLabel}`}>Right now</p>
          <h2 className={`mt-2 text-2xl font-semibold tracking-tight ${toneClasses.nowTitle}`}>
            {highlightedSquad
              ? hasUnread
                ? `${highlightedSquad.unreadCount} update${highlightedSquad.unreadCount === 1 ? '' : 's'}`
                : highlightedSquad.name
              : 'Play home'}
          </h2>
          <p className={`mt-1.5 max-w-2xl text-sm font-medium ${toneClasses.nowText}`}>
            {highlightedSquad
              ? hasUnread
                ? highlightedSquad.name
                : `${highlightedSquad.memberCount || 0} members`
              : 'Create or join squads when you are ready.'}
          </p>

          <div className="mt-5 flex flex-wrap gap-3">
            {highlightedSquad ? (
              <Button onClick={() => onOpenSquad(highlightedSquad.squadId)}>
                Open squad
              </Button>
            ) : (
              <Button onClick={onOpenSquads}>
                Open squads
              </Button>
            )}
            <Button variant="outline" onClick={onOpenPlay}>
              Log play
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CommunityTopMetric({
  label,
  value,
}: {
  label: string;
  value: number;
}) {
  return (
    <div className="rounded-2xl border border-border bg-surface-elevated/20 px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">{label}</p>
      <p className="mt-1.5 text-2xl font-semibold tracking-tight text-text">{value}</p>
    </div>
  );
}

function CommunitySquadsPanel({
  snapshot,
  onOpenSquad,
  onOpenSquads,
}: {
  snapshot: CommunityDashboardSnapshotResponse;
  onOpenSquad: (squadId: number) => void;
  onOpenSquads: () => void;
}) {

  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle>Your squads</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">

        {snapshot.squads.items.length > 0 ? (
          <div className="space-y-3">
            {snapshot.squads.items.map((squad) => (
              <div key={squad.squadId} className="rounded-[24px] border border-border bg-surface-elevated/20 p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-sm font-semibold text-primary">
                        {(squad.name || 'S').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate text-base font-semibold text-text">{squad.name}</p>
                          <span
                            className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${squadRoleChipClasses(
                              squad.userRole,
                            )}`}
                          >
                            {formatRole(squad.userRole)}
                          </span>
                          {squad.sport ? (
                            <span className="rounded-full border border-border bg-surface px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-text-subtle">
                              {formatCodeLabel(squad.sport)}
                            </span>
                          ) : null}
                          {squad.privacyType ? (
                            <span className="rounded-full border border-border bg-surface px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-text-subtle">
                              {formatCodeLabel(squad.privacyType)}
                            </span>
                          ) : null}
                          {squad.linkedProgramId ? (
                            <span className="rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                              Program linked
                            </span>
                          ) : squad.tenantId ? (
                            <span className="rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-sky-700">
                              Org linked
                            </span>
                          ) : null}
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.16em] text-text-subtle">
                          <span>{squad.memberCount || 0} members</span>
                          <span>{squad.lastMessageAt ? formatRelativeTime(squad.lastMessageAt) : 'No updates'}</span>
                          {(squad.unreadCount || 0) > 0 ? (
                            <span className="rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-primary">
                              {squad.unreadCount} update{(squad.unreadCount || 0) === 1 ? '' : 's'}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-wrap gap-2">
                    <Button size="sm" onClick={() => onOpenSquad(squad.squadId)}>
                      {getCommunitySquadActionLabel(squad.userRole)}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-border bg-surface-elevated/20 p-5">
            <p className="font-semibold text-text">No squads yet</p>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <Button onClick={onOpenSquads}>Open all squads</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CommunityPlayPanel({
  play,
  onOpenPlay,
}: {
  play: CommunityDashboardSnapshotResponse['play'];
  onOpenPlay: () => void;
}) {
  const recentResults = play.recentResults.slice(0, 3);
  const hasPlaySummary =
    play.recentScorecardsCount > 0 ||
    play.recentCourtsTracked > 0 ||
    recentResults.length > 0 ||
    Boolean(play.latestRecordedAt);

  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle>Play snapshot</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasPlaySummary ? (
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <CommunityTopMetric label="Scorecards" value={play.recentScorecardsCount} />
            <CommunityTopMetric label="Courts" value={play.recentCourtsTracked} />
            <CommunityTopMetric label="Recent" value={recentResults.length} />
            <div className="rounded-2xl border border-border bg-surface-elevated/20 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Updated</p>
              <p className="mt-1.5 text-base font-semibold tracking-tight text-text">
                {play.latestRecordedAt ? formatRelativeTime(play.latestRecordedAt) : 'None'}
              </p>
            </div>
          </div>
        ) : null}

        {recentResults.length > 0 ? (
          <div className="space-y-3">
            {recentResults.map((result) => (
              <button
                key={result.sessionId}
                type="button"
                onClick={onOpenPlay}
                className="flex w-full items-start gap-4 rounded-2xl border border-border bg-surface-elevated/20 p-4 text-left transition-colors hover:border-primary/30 hover:bg-surface-elevated"
              >
                <div className="rounded-2xl bg-primary/10 p-3 text-primary">
                  <Medal className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-text">{result.title}</p>
                      <p className="mt-1 text-sm text-text-muted">{result.squadName || 'TARK play'}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-sm font-semibold text-primary">
                        {result.scoreCompactSummary || 'No score'}
                      </p>
                      <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-text-subtle" />
                    </div>
                  </div>
                  <p className="mt-2 text-xs uppercase tracking-[0.18em] text-text-subtle">
                    {[result.courtName, formatRelativeTime(result.completedAt || result.startTime)].filter(Boolean).join(' • ')}
                  </p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-border bg-surface-elevated/20 p-5">
            <p className="font-semibold text-text">No scorecards yet</p>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <Button onClick={onOpenPlay}>Open Play</Button>
        </div>
      </CardContent>
    </Card>
  );
}

type LiveStructuredSetScore = {
  setOrder: number;
  leftGames: string;
  rightGames: string;
  leftTiebreak: string | null;
  rightTiebreak: string | null;
  isCompleted: boolean;
  isCurrent: boolean;
};

function CommunityBuzzRail({
  onOpenBuzz,
}: {
  onOpenBuzz: () => void;
}) {
  const railRef = useRef<HTMLDivElement | null>(null);
  const [isRailVisible, setIsRailVisible] = useState(false);

  useEffect(() => {
    const railNode = railRef.current;
    if (!railNode || typeof IntersectionObserver === 'undefined') {
      setIsRailVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const nextVisible = entries.some((entry) => entry.isIntersecting || entry.intersectionRatio > 0);
        setIsRailVisible(nextVisible);
      },
      {
        rootMargin: '220px 0px',
        threshold: 0.01,
      }
    );

    observer.observe(railNode);
    return () => observer.disconnect();
  }, []);

  const { feedQuery, liveQuery } = useWelcomeBuzzRail(isRailVisible);
  const pages = feedQuery.data?.pages ?? [];
  const visiblePages = useMemo(
    () => pages.slice(0, COMMUNITY_BUZZ_RAIL_MAX_PAGES),
    [pages]
  );
  const firstPage = visiblePages[0] ?? null;
  const top = useMemo(
    () => mergeBuzzTopSection(firstPage?.top ?? null, liveQuery.data ?? null),
    [firstPage?.top, liveQuery.data]
  );
  const bodyBlocks = useMemo(
    () =>
      visiblePages
        .flatMap((page) => page.body ?? [])
        .filter((block) => shouldRenderBuzzRailBlock(block)),
    [visiblePages]
  );
  const hasReachedRailPageBudget = pages.length >= COMMUNITY_BUZZ_RAIL_MAX_PAGES;
  const canLoadMoreWithinRail = Boolean(feedQuery.hasNextPage) && !hasReachedRailPageBudget;

  const updatedAt = liveQuery.data?.generatedAt ?? firstPage?.generatedAt ?? null;
  const updatedLabel = updatedAt ? formatRelativeTime(updatedAt) : null;
  const hasAnyBuzz =
    Boolean(top?.pulseStrip?.items?.length) ||
    Boolean(top?.liveRail?.items?.length) ||
    Boolean(top?.hero?.items?.length) ||
    bodyBlocks.length > 0;

  const openBuzzItem = (item: BuzzFeedV1Item) => {
    const destinationUrl = resolveBuzzFeedItemUrl(item);
    if (destinationUrl) {
      if (/^https?:\/\//i.test(destinationUrl)) {
        window.open(destinationUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      if (destinationUrl.startsWith('/')) {
        window.location.assign(destinationUrl);
        return;
      }
    }

    onOpenBuzz();
  };

  const handleRailScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!canLoadMoreWithinRail || feedQuery.isFetchingNextPage) {
      return;
    }

    const target = event.currentTarget;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining < 280) {
      void feedQuery.fetchNextPage();
    }
  };

  return (
    <div ref={railRef}>
      <Card variant="default" className="overflow-hidden border-primary/20 bg-surface">
        <CardHeader className="border-b border-border/70 pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-red-500" />
                <CardTitle>Sports feed</CardTitle>
              </div>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">
                Scores, highlights, rankings
              </p>
              {updatedLabel ? (
                <p className="mt-1 text-[11px] text-text-subtle">Updated {updatedLabel}</p>
              ) : null}
            </div>
            <Button
              variant="ghost"
              onClick={onOpenBuzz}
              className="h-auto shrink-0 px-0 py-0 text-sm font-semibold text-primary hover:bg-transparent hover:text-primary/80"
            >
              Open feed
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isRailVisible && feedQuery.isLoading ? (
            <div className="flex items-center gap-3 px-5 pb-5">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <p className="text-sm text-text-muted">Loading live BUZZ…</p>
            </div>
          ) : null}

          {isRailVisible && !feedQuery.isLoading && feedQuery.isError && !hasAnyBuzz ? (
            <div className="px-5 pb-5">
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                BUZZ is unavailable right now. Try again and we’ll restore the live rail.
              </div>
              <div className="mt-3">
                <Button variant="outline" onClick={() => void feedQuery.refetch()}>
                  Retry
                </Button>
              </div>
            </div>
          ) : null}

          {hasAnyBuzz ? (
            <div
              onScroll={handleRailScroll}
              className="space-y-5 px-5 pb-5 xl:max-h-[calc(100vh-11rem)] xl:overflow-y-auto xl:overscroll-contain xl:pr-3"
            >
              <BuzzRailPulseStrip block={top?.pulseStrip ?? null} onOpenItem={openBuzzItem} />
              <BuzzRailLiveScores block={top?.liveRail ?? null} onOpenItem={openBuzzItem} />
              <BuzzRailHero block={top?.hero ?? null} onOpenItem={openBuzzItem} />
              {bodyBlocks.map((block, index) => (
                <BuzzRailBodyBlock
                  key={`${block.blockId ?? 'buzz-block'}-${index}`}
                  block={block}
                  onOpenItem={openBuzzItem}
                />
              ))}

              {liveQuery.isError ? (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-medium text-amber-700">
                  Live scores may be a little stale right now.
                </div>
              ) : null}

              {feedQuery.isFetchingNextPage ? (
                <div className="flex items-center justify-center gap-2 rounded-2xl border border-border bg-surface-elevated/10 px-4 py-3 text-sm text-text-muted">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  Loading more BUZZ…
                </div>
              ) : null}

              {canLoadMoreWithinRail && !feedQuery.isFetchingNextPage ? (
                <div className="flex justify-center xl:justify-start">
                  <Button variant="outline" onClick={() => void feedQuery.fetchNextPage()}>
                    Load more Buzz
                  </Button>
                </div>
              ) : null}

              {feedQuery.hasNextPage && hasReachedRailPageBudget && !feedQuery.isFetchingNextPage ? (
                <div className="flex justify-center xl:justify-start">
                  <Button variant="ghost" onClick={onOpenBuzz}>
                    Open full Buzz
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

function mergeBuzzTopSection(
  top: BuzzFeedV1Response['top'],
  liveRefresh: BuzzLiveRefreshResponse | null
): BuzzFeedV1Response['top'] {
  if (!top && !liveRefresh) {
    return null;
  }

  return {
    ...(top ?? {}),
    pulseStrip: liveRefresh?.pulseStrip ?? top?.pulseStrip ?? null,
    liveRail: liveRefresh?.liveRail ?? top?.liveRail ?? null,
    hero: top?.hero ?? null,
  };
}

function BuzzRailSectionEyebrow({
  label,
  tone = 'default',
}: {
  label: string;
  tone?: 'default' | 'live';
}) {
  return (
    <div className="flex items-center gap-2">
      <span className={tone === 'live' ? 'h-2 w-2 rounded-full bg-red-500' : 'h-2 w-2 rounded-full bg-primary'} />
      <p className={tone === 'live'
        ? 'text-[11px] font-semibold uppercase tracking-[0.18em] text-red-500'
        : 'text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle'}>
        {label}
      </p>
    </div>
  );
}

function BuzzRailPulseStrip({
  block,
  onOpenItem,
}: {
  block: BuzzFeedV1Block | null;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const items = (block?.items ?? []).slice(0, 6);
  if (items.length === 0) {
    return null;
  }

  return (
    <section className="space-y-2">
      <BuzzRailSectionEyebrow label="Happening now" />
      <div className="-mx-1 overflow-x-auto pb-1">
        <div className="flex gap-2 px-1 snap-x snap-mandatory">
          {items.map((item) => (
            <button
              key={item.itemId || item.headline}
              type="button"
              onClick={() => onOpenItem(item)}
              className="w-[164px] shrink-0 snap-start rounded-xl border border-border bg-white px-2.5 py-2.5 text-left transition-colors hover:border-primary/30 hover:bg-surface-elevated sm:w-[172px] xl:w-[164px] 2xl:w-[172px]"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-[9px] font-extrabold uppercase tracking-[0.18em] text-text-subtle">
                  {getPulseEyebrow(item)}
                </p>
                {isLiveBuzzItem(item) ? (
                  <span className="rounded-full bg-red-500 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.16em] text-white">
                    Live
                  </span>
                ) : null}
              </div>
              <p className="mt-2 line-clamp-2 text-[12px] font-bold leading-4 text-text">
                {getHeadline(item)}
              </p>
              <p className="mt-1.5 text-[10px] text-text-muted">
                {getPulseFootnote(item)}
              </p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function BuzzRailLiveScores({
  block,
  onOpenItem,
}: {
  block: BuzzFeedV1Block | null;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const items = (block?.items ?? []).slice(0, 6);
  if (items.length === 0) {
    return null;
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <BuzzRailSectionEyebrow label="Live now" tone="live" />
        <p className="text-[11px] uppercase tracking-[0.16em] text-text-subtle">
          {items.length} court{items.length === 1 ? '' : 's'}
        </p>
      </div>

      <div className="-mx-1 overflow-x-auto pb-1">
        <div className="flex gap-2 px-1 snap-x snap-mandatory">
          {items.map((item) => {
            const setScores = getLiveStructuredSetScores(item);
            const pointPair = getLivePointPair(item);

            return (
              <button
                key={item.itemId || item.headline}
                type="button"
                onClick={() => onOpenItem(item)}
                className="w-[208px] shrink-0 snap-start rounded-[14px] border border-border bg-white px-2.5 py-2 text-left shadow-[var(--shadow-card)] transition-colors hover:border-primary/30 hover:bg-surface-elevated/20"
              >
                <div className="mb-1.5 space-y-0.5">
                  <p className="truncate text-[10px] font-extrabold tracking-[-0.01em] text-text">
                    {getLiveCompetitionLabel(item)}
                  </p>
                  {getLiveStageLabel(item) ? (
                    <p className="truncate text-[9px] font-bold text-text-muted">{getLiveStageLabel(item)}</p>
                  ) : null}
                </div>

                <div className="mb-0.5 grid grid-cols-[minmax(0,1fr)_18px_18px_18px_28px] items-center">
                  <div className="mr-1" />
                  <p className="text-center text-[9px] font-extrabold text-text-muted">1</p>
                  <p className="text-center text-[9px] font-extrabold text-text-muted">2</p>
                  <p className="text-center text-[9px] font-extrabold text-text-muted">3</p>
                  <p className="text-center text-[9px] font-black text-text-secondary">PTS</p>
                </div>

                {[0, 1].map((participantIndex) => (
                  <div key={`participant-${participantIndex}`} className="grid grid-cols-[minmax(0,1fr)_18px_18px_18px_28px] items-center py-[3px]">
                    <div className="mr-1 flex min-w-0 items-center gap-[5px]">
                      <span
                        className={isParticipantServing(item, participantIndex)
                          ? 'h-1.5 w-1.5 shrink-0 rounded-full bg-primary ring-4 ring-primary/10 animate-pulse'
                          : 'h-1.5 w-1.5 shrink-0 rounded-full bg-transparent'}
                      />
                      <span className="truncate text-[11px] font-black text-text">
                        {getCompactParticipantName(item, participantIndex)}
                      </span>
                    </div>
                    {Array.from({ length: 3 }, (_, setIndex) => {
                      const setScore = setScores[setIndex];
                      const setValue = getLiveSetDisplayValue(setScore, participantIndex);
                      return (
                        <p
                          key={`set-${participantIndex}-${setIndex}`}
                          className={getLiveSetClassName(setScore, setValue)}
                        >
                          {setValue}
                        </p>
                      );
                    })}
                    <p
                      className={
                        pointPair
                          ? 'text-center text-[14px] font-black tabular-nums text-primary'
                          : 'text-center text-[14px] font-black tabular-nums text-text-muted'
                      }
                    >
                      {participantIndex === 0 ? pointPair?.left ?? '-' : pointPair?.right ?? '-'}
                    </p>
                  </div>
                ))}

                <p className="mt-1.5 truncate text-[9px] font-bold text-text-secondary">
                  {getLiveFooter(item)}
                </p>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BuzzRailHero({
  block,
  onOpenItem,
}: {
  block: BuzzFeedV1Block | null;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const item = block?.items?.[0];
  if (!item) {
    return null;
  }

  return (
    <section className="space-y-2">
      <BuzzRailSectionEyebrow label="Featured" />
      <button
        type="button"
        onClick={() => onOpenItem(item)}
        className="flex min-h-[112px] overflow-hidden rounded-[14px] border border-border bg-white text-left transition-colors hover:border-primary/30 hover:bg-surface-elevated"
      >
        {getImageUrl(item) ? (
          <img
            src={getImageUrl(item) ?? ''}
            alt=""
            className="min-h-[112px] w-[38%] object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex min-h-[112px] w-[38%] items-center justify-center bg-primary/10 text-primary">
            <Newspaper className="h-7 w-7" />
          </div>
        )}

        <div className="flex flex-1 flex-col justify-center px-2.5 py-2">
          <div className="mb-1.5 flex items-start justify-between gap-2">
            <div className="min-w-0 space-y-1">
              {getReasonLabel(item) ? (
                <span className="inline-flex rounded-full bg-primary/10 px-2 py-1 text-[9px] font-extrabold uppercase tracking-[0.14em] text-primary">
                  {getReasonLabel(item)}
                </span>
              ) : null}
              <p className="truncate text-[9px] font-bold text-text-muted">
                {getSourceLabel(item)}
              </p>
            </div>
          </div>

          <p className="text-[14px] font-black leading-[1.35] text-text">
            {getHeadline(item)}
          </p>

          {getSummary(item) ? (
            <p className="mt-1 line-clamp-2 text-[11px] leading-[1.35] text-text-secondary">
              {getSummary(item)}
            </p>
          ) : null}

          <p className="mt-1.5 text-[10px] text-text-muted">
            {formatDateTimeLabel(getPublishedAt(item) || getUpdatedAt(item))}
          </p>
        </div>
      </button>
    </section>
  );
}

function BuzzRailBodyBlock({
  block,
  onOpenItem,
}: {
  block: BuzzFeedV1Block;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  switch (block.blockType) {
    case 'RESULTS_TICKER_BLOCK':
      return <BuzzRailResultsBlock block={block} onOpenItem={onOpenItem} />;
    case 'NEWS_BLOCK':
      return <BuzzRailNewsBlock block={block} onOpenItem={onOpenItem} />;
    case 'VIDEO_CAROUSEL_BLOCK':
    case 'LESSONS_BLOCK':
      return <BuzzRailMediaBlock block={block} onOpenItem={onOpenItem} />;
    case 'RANKINGS_BLOCK':
      return <BuzzRailRankingsBlock block={block} onOpenItem={onOpenItem} />;
    case 'SPONSORED_BLOCK':
    case 'CTA_BLOCK':
      return null;
    default:
      return <BuzzRailGenericBlock block={block} onOpenItem={onOpenItem} />;
  }
}

function BuzzRailResultsBlock({
  block,
  onOpenItem,
}: {
  block: BuzzFeedV1Block;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const items = (block.items ?? []).slice(0, 6);
  if (items.length === 0) {
    return null;
  }

  return (
    <section className="space-y-2">
      <BuzzRailSectionEyebrow label={block.title || 'Results'} />
      <div className="-mx-1 overflow-x-auto pb-1">
        <div className="flex gap-3 px-1 snap-x snap-mandatory">
          {items.map((item) => (
            <button
              key={item.itemId || item.headline}
              type="button"
              onClick={() => onOpenItem(item)}
              className="w-[188px] shrink-0 snap-start rounded-2xl border border-border bg-white p-4 text-left transition-colors hover:border-primary/30 hover:bg-surface-elevated"
            >
              <p className="line-clamp-2 text-sm font-bold leading-5 text-text">
                {getHeadline(item)}
              </p>
              <p className="mt-3 text-base font-black text-primary">
                {getScoreSummary(item)}
              </p>
              <p className="mt-1 text-[11px] font-semibold text-text-secondary">
                {getScoreStatus(item) || [item.event?.competition, item.event?.stage].filter(Boolean).join(' • ')}
              </p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function BuzzRailNewsBlock({
  block,
  onOpenItem,
}: {
  block: BuzzFeedV1Block;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const items = block.items ?? [];
  if (items.length === 0) {
    return null;
  }

  const lead = items[0];
  const rows = items.slice(1, 5);

  return (
    <section className="space-y-2.5">
      <BuzzRailSectionEyebrow label={block.title || 'News'} />

      <button
        type="button"
        onClick={() => onOpenItem(lead)}
        className="flex items-center gap-3 rounded-[15px] border border-border bg-white px-3 py-[10px] text-left transition-colors hover:border-primary/30 hover:bg-surface-elevated"
      >
        {getImageUrl(lead) ? (
          <img
            src={getImageUrl(lead) ?? ''}
            alt=""
            className="h-[63px] w-[84px] shrink-0 rounded-[9px] object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-[63px] w-[84px] shrink-0 items-center justify-center rounded-[9px] bg-primary/10 text-primary">
            <Newspaper className="h-5 w-5" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-[11px] font-extrabold uppercase tracking-[0.12em] text-text-muted">
            {getSourceLabel(lead)}
          </p>
          <p className="line-clamp-2 text-[15px] font-bold leading-[1.3] text-text">
            {getHeadline(lead)}
          </p>
          <p className="mt-1 text-[11px] text-text-secondary">
            {formatDateTimeLabel(getPublishedAt(lead) || getUpdatedAt(lead))}
          </p>
        </div>
      </button>

      {rows.length > 0 ? (
        <div className="space-y-1.5">
          {rows.map((item) => (
            <button
              key={item.itemId || item.headline}
              type="button"
              onClick={() => onOpenItem(item)}
              className="flex w-full items-center gap-3 rounded-[15px] border border-border bg-white px-3 py-[10px] text-left transition-colors hover:border-primary/30 hover:bg-surface-elevated"
            >
              {getImageUrl(item) ? (
                <img
                  src={getImageUrl(item) ?? ''}
                  alt=""
                  className="h-[63px] w-[84px] shrink-0 rounded-[9px] object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-[63px] w-[84px] shrink-0 items-center justify-center rounded-[9px] bg-primary/10 text-primary">
                  <Newspaper className="h-4 w-4" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="mb-1 text-[10px] font-extrabold uppercase tracking-[0.12em] text-text-muted">
                  {getSourceLabel(item)}
                </p>
                <p className="line-clamp-2 text-[14px] font-bold leading-[1.3] text-text">
                  {getHeadline(item)}
                </p>
                <p className="mt-1 text-[11px] text-text-secondary">
                  {formatDateTimeLabel(getPublishedAt(item) || getUpdatedAt(item))}
                </p>
              </div>
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function BuzzRailMediaBlock({
  block,
  onOpenItem,
}: {
  block: BuzzFeedV1Block;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const items = (block.items ?? []).slice(0, 6);
  if (items.length === 0) {
    return null;
  }

  const icon = block.blockType === 'LESSONS_BLOCK' ? Activity : PlayCircle;

  return (
    <section className="space-y-2">
      <BuzzRailSectionEyebrow label={block.title || (block.blockType === 'LESSONS_BLOCK' ? 'Lessons' : 'Highlights')} />
      <div className="-mx-1 overflow-x-auto pb-1">
        <div className="flex gap-3 px-1 snap-x snap-mandatory">
          {items.map((item) => {
            const Icon = icon;
            const durationLabel = getDurationChipLabel(item);
            return (
              <button
                key={item.itemId || item.headline}
                type="button"
                onClick={() => onOpenItem(item)}
                className="w-[210px] shrink-0 snap-start overflow-hidden rounded-2xl border border-border bg-white text-left transition-colors hover:border-primary/30 hover:bg-surface-elevated"
              >
                <div className="relative">
                  {getImageUrl(item) ? (
                    <img
                      src={getImageUrl(item) ?? ''}
                      alt=""
                      className="h-[122px] w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-[122px] items-center justify-center bg-primary/10 text-primary">
                      <Icon className="h-8 w-8" />
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-black/45 text-white">
                      <PlayCircle className="h-4 w-4 fill-current" />
                    </div>
                  </div>
                  {durationLabel ? (
                    <span className="absolute bottom-2 right-2 rounded-md bg-black/70 px-2 py-1 text-[10px] font-extrabold text-white">
                      {durationLabel}
                    </span>
                  ) : null}
                </div>
                <div className="space-y-1 p-3">
                  <p className="line-clamp-2 text-[13px] font-bold leading-[1.35] text-text">
                    {getHeadline(item)}
                  </p>
                  <p className="text-[11px] text-text-secondary">
                    {block.blockType === 'LESSONS_BLOCK' ? getLessonLabel(item) : getSourceLabel(item)}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BuzzRailRankingsBlock({
  block,
  onOpenItem,
}: {
  block: BuzzFeedV1Block;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const items = (block.items ?? []).slice(0, 6);
  if (items.length === 0) {
    return null;
  }

  if (block.displayStyle === 'HORIZONTAL_CAROUSEL') {
    return (
      <section className="space-y-2">
        <BuzzRailSectionEyebrow label={block.title || 'Rankings'} />
        <div className="-mx-1 overflow-x-auto pb-1">
          <div className="flex gap-2.5 px-1 snap-x snap-mandatory">
            {items.map((item, index) => (
              <button
                key={item.itemId || item.headline || index}
                type="button"
                onClick={() => onOpenItem(item)}
                className="relative min-h-[152px] w-[110px] shrink-0 snap-start overflow-hidden rounded-2xl border border-border bg-slate-900 text-left"
              >
                {getHeadshotUrl(item) ? (
                  <img
                    src={getHeadshotUrl(item) ?? ''}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-800 text-white/80">
                    <Users className="h-8 w-8" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-slate-950/5 via-slate-950/25 to-slate-950/90" />
                <div className="relative flex min-h-[152px] flex-col justify-between p-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[13px] font-black text-white">
                      {getRank(item)}
                    </p>
                    <p className={getRankingMovementClassName(item)}>
                      {getRankingMovementDisplay(item) || ' '}
                    </p>
                  </div>
                  <div>
                    <p className="line-clamp-2 text-[11px] font-extrabold leading-[1.25] text-white">
                      {getHeadline(item)}
                    </p>
                    <p className="mt-1 text-[9px] text-white/80">
                      {[getRankingCountry(item), getRankingPointsLabel(item)].filter(Boolean).join(' • ')}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-2">
      <BuzzRailSectionEyebrow label={block.title || 'Rankings'} />
      <div className="overflow-hidden rounded-[22px] border border-border bg-white">
        {items.map((item, index) => (
          <button
            key={item.itemId || item.headline || index}
            type="button"
            onClick={() => onOpenItem(item)}
            className="flex w-full items-center justify-between gap-3 border-b border-border px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-surface-elevated"
          >
            <div className="flex items-center gap-3.5">
              <p className="min-w-[34px] text-base font-black text-primary">
                {getRank(item)}
              </p>
              <div className="min-w-0">
                <p className="truncate text-[13px] font-bold text-text">
                  {getHeadline(item)}
                </p>
                <p className="mt-0.5 text-[11px] text-text-secondary">
                  {getRankingLabel(item)}
                </p>
              </div>
            </div>
            {getRankingMovementDisplay(item) ? (
              <p className={getRankingMovementClassName(item)}>
                {getRankingMovementDisplay(item)}
              </p>
            ) : null}
          </button>
        ))}
      </div>
    </section>
  );
}

function BuzzRailGenericBlock({
  block,
  onOpenItem,
}: {
  block: BuzzFeedV1Block;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const items = (block.items ?? []).slice(0, 4);
  if (items.length === 0) {
    return null;
  }

  return (
    <section className="space-y-2">
      <BuzzRailSectionEyebrow label={block.title || 'More stories'} />
      <div className="space-y-2">
        {items.map((item) => (
          <button
            key={item.itemId || item.headline}
            type="button"
            onClick={() => onOpenItem(item)}
            className="flex w-full items-start gap-3 rounded-[15px] border border-border bg-white p-3 text-left transition-colors hover:border-primary/30 hover:bg-surface-elevated"
          >
            {getImageUrl(item) ? (
              <img
                src={getImageUrl(item) ?? ''}
                alt=""
                className="h-14 w-14 shrink-0 rounded-xl object-cover"
                loading="lazy"
              />
            ) : (
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Radio className="h-4 w-4" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="line-clamp-2 text-sm font-bold leading-5 text-text">
                {getHeadline(item)}
              </p>
              <p className="mt-1 text-[11px] text-text-secondary">
                {[getSourceLabel(item), formatDateTimeLabel(getPublishedAt(item) || getUpdatedAt(item))].filter(Boolean).join(' • ')}
              </p>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

function shouldRenderBuzzRailBlock(block: BuzzFeedV1Block) {
  return !['SPONSORED_BLOCK', 'CTA_BLOCK'].includes(block.blockType || '');
}

function resolveBuzzFeedItemUrl(item: BuzzFeedV1Item): string | null {
  const primaryUrl = item.actions?.primary?.url?.trim();
  if (primaryUrl) {
    return primaryUrl;
  }

  const secondary = item.actions?.secondary?.find((action) => action.url?.trim());
  return secondary?.url?.trim() || null;
}

function getHeadline(item: BuzzFeedV1Item) {
  return stringValue(item.headline) || 'BUZZ';
}

function getSourceLabel(item: BuzzFeedV1Item) {
  return stringValue(item.source?.sourceLabel) || 'TARK';
}

function getSummary(item: BuzzFeedV1Item) {
  return stringValue(item.summary);
}

function getPublishedAt(item: BuzzFeedV1Item) {
  return stringValue(item.publishedAt);
}

function getUpdatedAt(item: BuzzFeedV1Item) {
  return stringValue(item.updatedAt);
}

function getReasonLabel(item: BuzzFeedV1Item) {
  return stringValue(item.reason?.label);
}

function getAttachmentImageUrl(item: BuzzFeedV1Item) {
  const attachments = (item as Record<string, unknown>).attachments;
  if (!Array.isArray(attachments)) {
    return undefined;
  }
  for (const attachment of attachments) {
    if (!attachment || typeof attachment !== 'object') {
      continue;
    }
    const record = attachment as Record<string, unknown>;
    const url = firstAllowedImageUrl(
      stringValue(record['thumbnailUrl']),
      stringValue(record['imageUrl']),
      stringValue(record['url']),
    );
    if (url) {
      return url;
    }
  }
  return undefined;
}

function getImageUrl(item: BuzzFeedV1Item) {
  const media = item.media as Record<string, unknown> | null | undefined;
  return firstAllowedImageUrl(
    stringValue(item.media?.thumbnailUrl),
    stringValue(media?.['imageUrl']),
    stringValue(media?.['posterUrl']),
    stringValue(item.thumbnailUrl),
    stringValue(item.imageUrl),
    stringValue(item.image),
    stringValue(item.imageFallback),
    getAttachmentImageUrl(item),
  );
}

function getPulseEyebrow(item: BuzzFeedV1Item) {
  return stringValue(item.itemType)?.replace(/_/g, ' ') || 'NOW';
}

function getPulseFootnote(item: BuzzFeedV1Item) {
  return getScoreStatus(item) || getReasonLabel(item) || getSourceLabel(item);
}

function isLiveBuzzItem(item: BuzzFeedV1Item) {
  return (stringValue(item.status) || '').toUpperCase() === 'LIVE' || getPulseFootnote(item).toUpperCase().includes('LIVE');
}

function getLiveCompetitionLabel(item: BuzzFeedV1Item) {
  return firstString(
    stringValue(item.event?.competition),
    stringValue(item.event?.title),
    getEventLabel(item),
    'Live Match'
  ) || 'Live Match';
}

function getLiveStageLabel(item: BuzzFeedV1Item) {
  return firstString(
    stringValue(item.event?.stage),
    getFallbackLiveStage(stringValue(item.event?.title), stringValue(item.event?.competition)),
  );
}

function getParticipants(item: BuzzFeedV1Item) {
  return Array.isArray(item.participants) ? item.participants : [];
}

function getEventLabel(item: BuzzFeedV1Item) {
  return firstString(
    stringValue(item.event?.title),
    stringValue(item.event?.competition),
    getSourceLabel(item),
  );
}

function getLocationLabel(item: BuzzFeedV1Item) {
  return firstString(
    stringValue(item.event?.locationLabel),
    getScoreStatus(item),
  );
}

function getParticipantName(item: BuzzFeedV1Item, index: number) {
  return stringValue(getParticipants(item)[index]?.displayName) || (index === 0 ? 'Player One' : 'Player Two');
}

function getCompactParticipantName(item: BuzzFeedV1Item, index: number) {
  return compactParticipantName(getParticipantName(item, index));
}

function compactParticipantName(displayName: string) {
  const slashParts = displayName
    .split('/')
    .map((part) => part.trim())
    .filter(Boolean);
  if (slashParts.length > 1) {
    return slashParts.map(lastToken).join(' / ');
  }
  return lastToken(displayName);
}

function lastToken(value: string) {
  const parts = value.trim().split(/\s+/);
  return parts[parts.length - 1] || value;
}

function isParticipantServing(item: BuzzFeedV1Item, index: number) {
  const participantId = stringValue(getParticipants(item)[index]?.participantId);
  const serverParticipantId = stringValue(item.score?.serverParticipantId);
  return Boolean(participantId && serverParticipantId && participantId === serverParticipantId);
}

function getScoreSummary(item: BuzzFeedV1Item) {
  return stringValue(item.score?.summaryText) || '0-0';
}

function getScoreStatus(item: BuzzFeedV1Item) {
  return stringValue(item.score?.statusText) || stringValue(item.status) || '';
}

function getLiveStructuredSetScores(item: BuzzFeedV1Item): LiveStructuredSetScore[] {
  const structuredScores = (item.scores ?? [])
    .map((entry, index) => parseLiveSetScore(entry, index + 1))
    .filter((entry): entry is Omit<LiveStructuredSetScore, 'isCompleted' | 'isCurrent'> => Boolean(entry))
    .sort((a, b) => a.setOrder - b.setOrder)
    .slice(0, 3);

  let baseScores = structuredScores.length ? structuredScores : parseSummarySetScores(item);
  const currentSetOrder = resolveCurrentSetOrder(item, baseScores);

  if (
    currentSetOrder != null
    && currentSetOrder > 0
    && currentSetOrder <= 3
    && !baseScores.some((entry) => entry.setOrder === currentSetOrder)
  ) {
    baseScores = [
      ...baseScores,
      {
        setOrder: currentSetOrder,
        leftGames: '-',
        rightGames: '-',
        leftTiebreak: null,
        rightTiebreak: null,
      },
    ];
  }

  return baseScores
    .sort((a, b) => a.setOrder - b.setOrder)
    .slice(0, 3)
    .map((entry, index, entries) => ({
      ...entry,
      isCompleted: !(currentSetOrder != null ? entry.setOrder === currentSetOrder : index === entries.length - 1) && isCompletedLiveSet(entry),
      isCurrent: currentSetOrder != null ? entry.setOrder === currentSetOrder : index === entries.length - 1,
    }));
}

function parseLiveSetScore(
  entry: NonNullable<BuzzFeedV1Item['scores']>[number],
  fallbackOrder: number
): Omit<LiveStructuredSetScore, 'isCompleted' | 'isCurrent'> | null {
  if (!entry) {
    return null;
  }

  const left = parseLiveSetSide(firstString(
    stringValue(entry.score_first),
    stringValue(entry.home_score),
  ));
  const right = parseLiveSetSide(firstString(
    stringValue(entry.score_second),
    stringValue(entry.away_score),
  ));

  if (left.games === '-' && right.games === '-') {
    return null;
  }

  return {
    setOrder: numberValue(entry.score_set) ?? fallbackOrder,
    leftGames: left.games,
    rightGames: right.games,
    leftTiebreak: left.tiebreak,
    rightTiebreak: right.tiebreak,
  };
}

function parseSummarySetScores(item: BuzzFeedV1Item): Array<Omit<LiveStructuredSetScore, 'isCompleted' | 'isCurrent'>> {
  const summaryText = getScoreSummary(item);
  if (!summaryText) {
    return [];
  }

  const matches = Array.from(summaryText.matchAll(/\b(\d{1,2}(?:\.\d{1,2})?)\s*[-:]\s*(\d{1,2}(?:\.\d{1,2})?)\b/g));
  return matches.slice(0, 3).map((match, index) => {
    const left = parseLiveSetSide(match[1] || undefined);
    const right = parseLiveSetSide(match[2] || undefined);
    return {
      setOrder: index + 1,
      leftGames: left.games,
      rightGames: right.games,
      leftTiebreak: left.tiebreak,
      rightTiebreak: right.tiebreak,
    };
  });
}

function resolveCurrentSetOrder(
  item: BuzzFeedV1Item,
  setScores: Array<Omit<LiveStructuredSetScore, 'isCompleted' | 'isCurrent'>>
) {
  const fromPointByPoint = getLatestPointByPointSetOrder(item);
  if (fromPointByPoint != null) {
    return fromPointByPoint;
  }

  const currentSet = [...setScores].reverse().find((entry) => !isCompletedLiveSet(entry));
  if (currentSet) {
    return currentSet.setOrder;
  }

  return setScores.length ? setScores[setScores.length - 1]?.setOrder ?? null : null;
}

function getLatestPointByPointSetOrder(item: BuzzFeedV1Item) {
  const games = Array.isArray(item.pointByPoint) ? item.pointByPoint : [];
  for (let index = games.length - 1; index >= 0; index -= 1) {
    const match = stringValue(games[index]?.set_number)?.match(/(\d+)/);
    if (match?.[1]) {
      return numberValue(match[1]);
    }
  }
  return null;
}

function isCompletedLiveSet(setScore: Omit<LiveStructuredSetScore, 'isCompleted' | 'isCurrent'>) {
  const left = numberValue(setScore.leftGames);
  const right = numberValue(setScore.rightGames);
  if (left == null || right == null) {
    return false;
  }

  const leadingGames = Math.max(left, right);
  const trailingGames = Math.min(left, right);
  if (leadingGames >= 6 && leadingGames - trailingGames >= 2) {
    return true;
  }
  if (leadingGames === 7 && (trailingGames === 5 || trailingGames === 6)) {
    return true;
  }
  if (left === 6 && right === 6) {
    const leftTiebreak = numberValue(setScore.leftTiebreak);
    const rightTiebreak = numberValue(setScore.rightTiebreak);
    if (leftTiebreak == null || rightTiebreak == null) {
      return false;
    }
    const leadingTiebreak = Math.max(leftTiebreak, rightTiebreak);
    const trailingTiebreak = Math.min(leftTiebreak, rightTiebreak);
    return leadingTiebreak >= 7 && leadingTiebreak - trailingTiebreak >= 2;
  }
  return false;
}

function getLiveSetDisplayValue(setScore: LiveStructuredSetScore | undefined, participantIndex: number) {
  if (!setScore) {
    return '-';
  }
  return participantIndex === 0 ? setScore.leftGames : setScore.rightGames;
}

function getLiveSetClassName(setScore: LiveStructuredSetScore | undefined, setValue: string) {
  if (!setScore || setValue === '-') {
    return 'text-center text-xs font-semibold tabular-nums text-text-subtle/60';
  }

  if (setScore.isCurrent) {
    return 'text-center text-sm font-black tabular-nums text-text';
  }

  if (setScore.isCompleted) {
    return 'text-center text-sm font-semibold tabular-nums text-text-muted';
  }

  return 'text-center text-sm font-semibold tabular-nums text-text';
}

function getLivePointPair(item: BuzzFeedV1Item) {
  return parseLivePointPair(getLatestLivePoint(item))
    ?? parseLivePointPair(stringValue(item.score?.currentGameScore))
    ?? null;
}

function getLiveFooter(item: BuzzFeedV1Item) {
  const latestGame = getLatestPointByPointGame(item);
  const parts = [
    normalizeSetLabel(stringValue(latestGame?.set_number)),
    normalizeGameLabel(stringValue(latestGame?.number_game)),
  ].filter(Boolean);
  return parts.join(' • ') || getCompactLiveStatus(item) || getLocationLabel(item) || '';
}

function getLatestPointByPointGame(item: BuzzFeedV1Item) {
  const games = Array.isArray(item.pointByPoint) ? item.pointByPoint : [];
  return games.length > 0 ? games[games.length - 1] ?? null : null;
}

function getLatestLivePoint(item: BuzzFeedV1Item) {
  const games = Array.isArray(item.pointByPoint) ? item.pointByPoint : [];
  for (let gameIndex = games.length - 1; gameIndex >= 0; gameIndex -= 1) {
    const points = Array.isArray(games[gameIndex]?.points) ? games[gameIndex]?.points ?? [] : [];
    for (let pointIndex = points.length - 1; pointIndex >= 0; pointIndex -= 1) {
      const point = points[pointIndex] as Record<string, unknown> | undefined;
      const normalized = normalizeLivePointString(stringValue(point?.score));
      if (normalized) {
        return normalized;
      }
    }
  }
  return normalizeLivePointString(stringValue(item.score?.currentGameScore));
}

function parseLivePointPair(rawValue?: string | null) {
  const normalized = normalizeLivePointString(rawValue);
  if (!normalized) {
    return null;
  }
  const match = normalized.match(/^(.+?)\s*[-:]\s*(.+)$/);
  if (!match?.[1] || !match[2]) {
    return null;
  }
  return {
    left: normalizePointValue(match[1]),
    right: normalizePointValue(match[2]),
  };
}

function normalizeLivePointString(value?: string | null) {
  if (!value) {
    return undefined;
  }
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (!normalized || normalized === '-' || normalized === '- -' || normalized === '- - -' || normalized === '- - - -') {
    return undefined;
  }
  return normalized;
}

function normalizePointValue(value: string) {
  const normalized = value.trim();
  if (!normalized) {
    return '-';
  }
  return normalized.toUpperCase() === 'AD' ? 'AD' : normalized;
}

function parseLiveSetSide(rawValue?: string | null) {
  if (!rawValue) {
    return { games: '-', gamesValue: null as number | null, tiebreak: null as string | null, tiebreakValue: null as number | null };
  }

  const trimmed = rawValue.trim();
  const match = trimmed.match(/^(\d+)(?:\.(\d+))?$/);
  if (!match) {
    return {
      games: trimmed,
      gamesValue: numberValue(trimmed),
      tiebreak: null,
      tiebreakValue: null,
    };
  }

  return {
    games: match[1] ?? '-',
    gamesValue: numberValue(match[1]) ?? null,
    tiebreak: match[2] ?? null,
    tiebreakValue: numberValue(match[2]) ?? null,
  };
}

function getFallbackLiveStage(title?: string | null, competition?: string | null) {
  if (!title) {
    return undefined;
  }
  if (!competition || title === competition) {
    return undefined;
  }
  return title;
}

function getCompactLiveStatus(item: BuzzFeedV1Item) {
  return getScoreStatus(item).replace(/\s*[•-]\s*[^•-]+ serving$/i, '').trim();
}

function normalizeSetLabel(value?: string | null) {
  if (!value) {
    return undefined;
  }
  if (/^set\s+\d+/i.test(value.trim())) {
    return value.trim();
  }
  return value.trim();
}

function normalizeGameLabel(value?: string | null) {
  if (!value) {
    return undefined;
  }
  return `Game ${value.trim()}`;
}

function getDurationChipLabel(item: BuzzFeedV1Item) {
  const mediaDuration = numberValue(item.media?.durationSec);
  const lessonDuration = numberValue(item.lessonMeta?.durationSec);
  const duration = mediaDuration ?? lessonDuration;
  return duration ? formatDuration(duration) : undefined;
}

function getLessonLabel(item: BuzzFeedV1Item) {
  const level = stringValue(item.lessonMeta?.level);
  const duration = numberValue(item.lessonMeta?.durationSec);
  const minutes = duration ? `${Math.max(1, Math.round(duration / 60))} min` : undefined;
  return [level, minutes].filter(Boolean).join(' • ') || getSourceLabel(item);
}

function getRank(item: BuzzFeedV1Item) {
  const rank = numberValue(item.rank);
  return rank ? `#${rank}` : '#-';
}

function getRankingLabel(item: BuzzFeedV1Item) {
  const tour = stringValue(item.rankingMeta?.tour);
  const points = numberValue(item.rankingMeta?.points);
  return [tour, points ? `${points.toLocaleString()} pts` : undefined].filter(Boolean).join(' • ');
}

function getRankingPointsLabel(item: BuzzFeedV1Item) {
  const points = numberValue(item.rankingMeta?.points);
  return points ? `${points.toLocaleString()} pts` : 'View profile';
}

function getRankingMovementDisplay(item: BuzzFeedV1Item) {
  const movement = stringValue(item.rankingMeta?.movement)?.trim() ?? '0';
  const normalized = movement.toLowerCase();
  if (!movement || normalized === '0' || normalized === '+0' || normalized === '-0' || normalized === 'same' || normalized === 'nc') {
    return undefined;
  }

  const movementValue = numberValue(item.rankingMeta?.movementValue);
  if ((normalized === 'up' || normalized === 'down') && movementValue != null) {
    return normalized === 'up' ? `+${movementValue}` : `-${movementValue}`;
  }
  return movement;
}

function getRankingCountry(item: BuzzFeedV1Item) {
  return firstString(
    stringValue(item.playerMeta?.country),
    getSummary(item),
    'Tennis player',
  ) || 'Tennis player';
}

function getHeadshotUrl(item: BuzzFeedV1Item) {
  return firstAllowedImageUrl(
    stringValue(item.playerMeta?.headshotUrl),
    getImageUrl(item),
  );
}

function getRankingMovementClassName(item: BuzzFeedV1Item) {
  const movement = getRankingMovementDisplay(item);
  if (!movement) {
    return 'text-[10px] font-extrabold text-white/80';
  }
  if (movement.startsWith('+')) {
    return 'text-[10px] font-extrabold text-primary';
  }
  if (movement.startsWith('-')) {
    return 'text-[10px] font-extrabold text-rose-300';
  }
  return 'text-[10px] font-extrabold text-white/80';
}

function formatDateTimeLabel(value?: string | null) {
  return value ? formatRelativeTime(value) : '';
}

function formatDuration(durationSec?: number | null) {
  if (!durationSec || Number.isNaN(durationSec)) {
    return '';
  }

  const minutes = Math.floor(durationSec / 60);
  const seconds = durationSec % 60;
  if (minutes <= 0) {
    return `${seconds}s`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function stringValue(value: unknown) {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return null;
}

function numberValue(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function firstString(...values: Array<string | null | undefined>) {
  for (const value of values) {
    if (value) {
      return value;
    }
  }
  return null;
}

function firstAllowedImageUrl(...values: Array<string | null | undefined>) {
  for (const value of values) {
    if (value && !isHotlinkBlockedBuzzImageUrl(value)) {
      return value;
    }
  }
  return null;
}

function isHotlinkBlockedBuzzImageUrl(value: string) {
  try {
    const parsed = new URL(value);
    return HOTLINK_BLOCKED_BUZZ_IMAGE_HOSTS.has(parsed.hostname.toLowerCase());
  } catch {
    return false;
  }
}

function CommunitySecondaryActions({
  membershipsCount,
  onOpenPros,
  onBecomePro,
  onJoinOrg,
}: {
  membershipsCount: number;
  onOpenPros: () => void;
  onBecomePro: () => void;
  onJoinOrg: () => void;
}) {
  return (
    <div className="border-t border-border/80 px-1 pt-4">
      <div className="flex flex-wrap items-center gap-2 text-sm text-text-muted">
        <button
          type="button"
          onClick={onOpenPros}
          className="rounded-full border border-border bg-surface-elevated/20 px-3 py-1.5 font-medium text-text transition-colors hover:border-primary/30 hover:text-primary"
        >
          PROs
        </button>

        <button
          type="button"
          onClick={onJoinOrg}
          className="rounded-full border border-border bg-surface-elevated/20 px-3 py-1.5 font-medium text-text transition-colors hover:border-primary/30 hover:text-primary"
        >
          Join org
        </button>

        <button
          type="button"
          onClick={onBecomePro}
          className="rounded-full border border-border bg-surface-elevated/20 px-3 py-1.5 font-medium text-text transition-colors hover:border-primary/30 hover:text-primary"
        >
          Become PRO
        </button>

        {membershipsCount > 0 ? (
          <span className="rounded-full border border-border bg-surface px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-text-subtle">
            {membershipsCount} org{membershipsCount === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function CommunityFocusPanel({
  squads,
  onOpenSquad,
  onOpenSquads,
  onOpenPlay,
}: {
  squads: CommunityDashboardSquadPreview[];
  onOpenSquad: (squadId: number) => void;
  onOpenSquads: () => void;
  onOpenPlay: () => void;
}) {
  const unreadCount = squads.reduce((sum, squad) => sum + (squad.unreadCount || 0), 0);

  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle>Squads</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3 text-sm text-text-muted">
          <span>{squads.length > 0 ? `${squads.length} active group${squads.length === 1 ? '' : 's'}` : 'No groups yet'}</span>
          {unreadCount > 0 ? (
            <>
              <span>•</span>
              <span>{unreadCount} unread</span>
            </>
          ) : null}
        </div>

        {squads.length > 0 ? (
          <div className="space-y-3">
            {squads.map((squad) => (
              <button
                key={squad.squadId}
                type="button"
                onClick={() => onOpenSquad(squad.squadId)}
                className="flex w-full items-start gap-4 rounded-2xl border border-border bg-surface-elevated/20 p-4 text-left transition-colors hover:border-primary/30 hover:bg-surface-elevated"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-sm font-semibold text-primary">
                  {(squad.name || 'S').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate font-semibold text-text">{squad.name}</p>
                    {(squad.unreadCount || 0) > 0 ? (
                      <span className="rounded-full bg-primary px-2 py-0.5 text-[11px] font-semibold [color:var(--color-on-primary)]">
                        {squad.unreadCount}
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-sm leading-6 text-text-muted">
                    {squad.lastMessage || 'Open this squad to jump back into the conversation.'}
                  </p>
                  <p className="mt-2 text-xs uppercase tracking-[0.18em] text-text-subtle">
                    {squad.memberCount} members • {formatRelativeTime(squad.lastMessageAt)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-border bg-surface-elevated/20 p-5">
            <p className="font-semibold text-text">No squads yet</p>
            <p className="mt-2 text-sm leading-6 text-text-muted">
              Open Squads to create one, join by code, or pick up a direct message.
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <Button onClick={onOpenSquads}>Open Squads</Button>
          <Button variant="outline" onClick={onOpenPlay}>Open Play</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function OrdersFocusPanel({
  workTabLabel,
  directoryLabel,
  requests,
  onOpenWork,
  onOpenDirectory,
}: {
  workTabLabel: string;
  directoryLabel: string;
  requests: Array<{
    requestId: number;
    requesterDisplayName: string;
    summary: string;
    contactMethod: string;
    requestStatus: string;
    updatedAt?: string | null;
    createdAt: string;
  }>;
  onOpenWork: () => void;
  onOpenDirectory: () => void;
}) {
  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle>{workTabLabel} overview</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {requests.length > 0 ? (
          <div className="space-y-3">
            {requests.slice(0, 4).map((request) => (
              <div key={request.requestId} className="rounded-2xl border border-border bg-surface-elevated/20 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-text">{request.requesterDisplayName}</p>
                    <p className="mt-1 text-sm text-text-muted">{request.summary}</p>
                  </div>
                  <div className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
                    {request.requestStatus}
                  </div>
                </div>
                <p className="mt-3 text-xs uppercase tracking-[0.18em] text-text-subtle">
                  {request.contactMethod} • {formatDateTime(request.updatedAt || request.createdAt)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-border bg-surface-elevated/20 p-5">
            <p className="font-semibold text-text">No requests yet</p>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <Button onClick={onOpenWork}>Open {workTabLabel}</Button>
          <Button variant="outline" onClick={onOpenDirectory}>Open {directoryLabel}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function getCoachReviewSummary(provider?: HomeDashboardProviderEntry | null) {
  switch (provider?.workflowState) {
    case 'DRAFT':
      return {
        title: 'Finish listing details',
        description: 'Photo, services, area, and contact are required.',
        icon: ClipboardList,
      };
    case 'IN_REVIEW':
      return {
        title: 'Listing in review',
        description: 'Private until TARK approves the coach profile.',
        icon: Clock3,
      };
    case 'APPROVED':
      return {
        title: 'Approved',
        description: 'Publish when ready.',
        icon: CheckCircle2,
      };
    case 'LIVE':
      return {
        title: 'Live in PROs',
        description: 'Visible in PROs.',
        icon: Radio,
      };
    case 'PAUSED':
      return {
        title: 'Listing paused',
        description: 'Hidden from PROs.',
        icon: Clock3,
      };
    case 'SUSPENDED':
      return {
        title: 'Needs updates',
        description: 'Open the listing for required changes.',
        icon: AlertTriangle,
      };
    default:
      return {
        title: 'Coach setup',
        description: 'Manage profile status and Coach Studio access.',
        icon: Calendar,
      };
  }
}

function ProviderPrimaryPanel({
  priority,
  provider,
  providerViews,
  providerContacts,
  linkedWorkspaceName,
  linkedWorkspaceLabel,
  workspaceStatus,
  isSwitchingWorkspace,
  isRequestingWorkspace,
  onOpenWorkspace,
  onRequestWorkspace,
  onManagePros,
  onOpenCommunityBrowse,
}: {
  priority: HomeDashboardPriority;
  provider: HomeDashboardProviderEntry | null;
  providerViews: number;
  providerContacts: number;
  linkedWorkspaceName: string | null;
  linkedWorkspaceLabel: string | null;
  workspaceStatus?: ProviderWorkspaceStatus | string | null;
  isSwitchingWorkspace: boolean;
  isRequestingWorkspace: boolean;
  onOpenWorkspace: () => void;
  onRequestWorkspace: () => void;
  onManagePros: () => void;
  onOpenCommunityBrowse: () => void;
}) {
  const providerState = getProviderStateCopy(provider);
  const workspaceLabel = linkedWorkspaceLabel || (priority === 'STRINGER' ? 'Work' : 'Schedule');
  const primaryActionLabel = linkedWorkspaceName ? `Open ${workspaceLabel}` : providerState.actionLabel;
  const workflowState = formatProviderWorkflowState(provider?.workflowState);
  const tone = getProviderTone(provider, priority);
  const toneClasses = getWelcomeToneClasses(tone);
  const isCoach = isCoachProvider(provider, priority);
  const reviewSummary = isCoach
    ? getCoachReviewSummary(provider)
    : {
        title: providerState.title,
        description: providerState.description,
        icon: priority === 'ACADEMY' ? Building2 : priority === 'STRINGER' ? Wrench : Sparkles,
      };
  const ReviewIcon = reviewSummary.icon;
  const isPublic = Boolean(provider?.publiclyVisible);
  const hasProviderAnalytics = isPublic && (providerViews > 0 || providerContacts > 0);
  const visibilityLabel = isPublic ? 'Live' : 'Private';
  const normalizedWorkspaceStatus = workspaceStatus || (provider?.tenantLinked ? 'ENABLED' : 'NOT_REQUESTED');
  const workspaceValue = linkedWorkspaceName || formatWorkspaceStatus(normalizedWorkspaceStatus);
  const checklist = isCoach
    ? ['Photo', 'Services', 'Area', 'Contact']
    : ['Identity', 'Services', 'Area', 'Studio'];
  const providerTypeLabel = formatProviderTypes(provider?.providerTypes);
  const isLiveProvider = provider?.workflowState === 'LIVE';
  const canRequestWorkspace =
    isCoach && isLiveProvider && !linkedWorkspaceName && normalizedWorkspaceStatus === 'NOT_REQUESTED';
  const workspaceRequested =
    !linkedWorkspaceName && ['REQUESTED', 'APPROVED'].includes(String(normalizedWorkspaceStatus));
  const actionHint =
    provider?.workflowState === 'IN_REVIEW'
      ? 'TARK approval is needed before this appears in PROs.'
      : provider?.workflowState === 'DRAFT'
        ? 'Complete missing items, then submit.'
        : provider?.workflowState === 'APPROVED'
          ? 'Approved. Publish when ready.'
          : isPublic
            ? 'Visible in PROs.'
            : 'Manage details and visibility.';

  if (isLiveProvider) {
    const liveEyebrow = isCoach ? 'Marketplace' : providerTypeLabel;
    const liveTitle = isCoach ? 'Public Profile' : `${providerTypeLabel} profile`;
    const liveDescription = isPublic ? 'Visible in PROs.' : 'Hidden from PROs.';

    return (
      <Card variant="default" padding="none" className="overflow-hidden border-primary/20">
        <CardContent className="grid gap-0 p-0 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className={`${toneClasses.now} border-r border-border p-5 md:p-6`}>
            <div className="flex min-w-0 items-start gap-3">
              <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${toneClasses.icon}`}>
                <ReviewIcon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${toneClasses.nowLabel}`}>{liveEyebrow}</p>
                <h2 className={`mt-1 text-2xl font-semibold tracking-tight ${toneClasses.nowTitle}`}>{liveTitle}</h2>
                <p className={`mt-1.5 max-w-2xl text-sm font-medium ${toneClasses.nowText}`}>{liveDescription}</p>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              {linkedWorkspaceName ? (
                <Button
                  onClick={onOpenWorkspace}
                  isLoading={isSwitchingWorkspace}
                >
                  {primaryActionLabel}
                </Button>
              ) : null}
              {canRequestWorkspace ? (
                <Button onClick={onRequestWorkspace} isLoading={isRequestingWorkspace}>
                  Request Coach Studio
                </Button>
              ) : null}
              {workspaceRequested ? (
                <Button variant="outline" disabled>
                  Coach Studio requested
                </Button>
              ) : null}
              <Button variant={linkedWorkspaceName || canRequestWorkspace || workspaceRequested ? 'outline' : 'primary'} onClick={onManagePros}>
                Manage Public Profile
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 bg-surface p-4 sm:grid-cols-3 lg:grid-cols-1">
            {hasProviderAnalytics ? (
              <>
                <CoachStatusTile label="Views" value={providerViews.toLocaleString()} />
                <CoachStatusTile label="Contacts" value={providerContacts.toLocaleString()} />
              </>
            ) : (
              <CoachStatusTile label="Visibility" value={visibilityLabel} />
            )}
            <CoachStatusTile label={workspaceLabel} value={workspaceValue} />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="default" padding="none" className="overflow-hidden">
      <CardContent className="grid gap-0 p-0 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className={`${toneClasses.now} border-r border-border p-5 md:p-6`}>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${toneClasses.icon}`}>
                <ReviewIcon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${toneClasses.nowLabel}`}>{providerTypeLabel}</p>
                <h2 className={`mt-1 text-2xl font-semibold tracking-tight ${toneClasses.nowTitle}`}>{reviewSummary.title}</h2>
                <p className={`mt-1.5 max-w-2xl text-sm font-medium ${toneClasses.nowText}`}>{reviewSummary.description}</p>
              </div>
            </div>

            <span className={`w-fit rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${toneClasses.nowBadge}`}>
              {workflowState}
            </span>
          </div>

          <div className="mt-6">
            <ProviderWorkflowBar workflowState={provider?.workflowState} tone={tone} />
          </div>

          <p className={`mt-5 text-[11px] font-semibold uppercase tracking-[0.18em] ${toneClasses.nowLabel}`}>
            What TARK needs
          </p>
          <div className="mt-2 grid gap-2 sm:grid-cols-4">
            {checklist.map((item) => (
              <div key={item} className={`rounded-lg border px-3 py-2 ${toneClasses.nowTile}`}>
                <p className="text-xs font-semibold text-text">{item}</p>
                <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-subtle">Required</p>
              </div>
            ))}
          </div>

          <p className={`mt-4 text-sm font-medium ${toneClasses.nowText}`}>{actionHint}</p>

          <div className="mt-5 flex flex-wrap gap-3">
            <Button onClick={onManagePros}>
              Manage Public Profile
            </Button>
            {linkedWorkspaceName ? (
              <Button
                variant="outline"
                onClick={onOpenWorkspace}
                isLoading={isSwitchingWorkspace}
              >
                {primaryActionLabel}
              </Button>
            ) : null}
            <Button
              variant="ghost"
              className={toneClasses.nowGhost}
              onClick={onOpenCommunityBrowse}
            >
              Browse PROs
            </Button>
          </div>
        </div>

        <div className="space-y-3 bg-surface p-4">
          <CoachStatusTile label="Visibility" value={visibilityLabel} />
          {hasProviderAnalytics ? (
            <>
              <CoachStatusTile label="Views" value={providerViews.toLocaleString()} />
              <CoachStatusTile label="Contacts" value={providerContacts.toLocaleString()} />
            </>
          ) : null}
          <CoachStatusTile label={workspaceLabel} value={workspaceValue} />
        </div>
      </CardContent>
    </Card>
  );
}

function ProviderWorkflowBar({
  workflowState,
  tone = 'coach',
}: {
  workflowState?: string | null;
  tone?: WelcomeTone;
}) {
  const toneClasses = getWelcomeToneClasses(tone);
  const steps = ['Draft', 'Review', 'Approved', 'Live'];
  const currentIndex = (() => {
    switch ((workflowState || '').toUpperCase()) {
      case 'DRAFT':
        return 0;
      case 'IN_REVIEW':
        return 1;
      case 'APPROVED':
        return 2;
      case 'LIVE':
      case 'PAUSED':
        return 3;
      default:
        return 0;
    }
  })();

  return (
    <div className="grid gap-2 sm:grid-cols-4">
      {steps.map((step, index) => {
        const isActive = index <= currentIndex;
        return (
          <div key={step} className="min-w-0">
            <div className={`h-1.5 rounded-full ${isActive ? toneClasses.nowTrackActive : toneClasses.nowTrackInactive}`} />
            <p className={`mt-1.5 truncate text-[11px] font-semibold uppercase tracking-[0.12em] ${isActive ? toneClasses.nowLabel : 'text-text-subtle'}`}>
              {step}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function CoachStatusTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface-elevated/20 px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-text-subtle">{label}</p>
      <p className="mt-2 truncate text-lg font-semibold text-text">{value}</p>
    </div>
  );
}

function CommunitySecondaryPanel({
  memberships,
  squadCount,
  onOpenSquads,
  onOpenPlay,
  onJoinOrg,
  onOpenProfile,
}: {
  memberships: TenantMembershipSummary[];
  squadCount: number;
  onOpenSquads: () => void;
  onOpenPlay: () => void;
  onJoinOrg: () => void;
  onOpenProfile: () => void;
}) {
  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle>Play</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl border border-border bg-surface-elevated/20 p-4">
            <p className="text-xs uppercase tracking-[0.18em] text-text-subtle">Squads</p>
            <p className="mt-2 text-2xl font-semibold tracking-tight text-text">{squadCount}</p>
          </div>
          <div className="rounded-2xl border border-border bg-surface-elevated/20 p-4">
            <p className="text-xs uppercase tracking-[0.18em] text-text-subtle">Studios</p>
            <p className="mt-2 text-2xl font-semibold tracking-tight text-text">{memberships.length}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={onOpenSquads}>Open Squads</Button>
          <Button variant="outline" onClick={onOpenPlay}>Open Play</Button>
          <Button variant="ghost" onClick={onJoinOrg}>Join Organization</Button>
          <Button variant="ghost" onClick={onOpenProfile}>Open Me</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CommunityGrowthPanel({
  memberships,
  onOpenBuzz,
  onOpenPros,
  onJoinOrg,
  onBecomePro,
  onOpenProfile,
}: {
  memberships: TenantMembershipSummary[];
  onOpenBuzz: () => void;
  onOpenPros: () => void;
  onJoinOrg: () => void;
  onBecomePro: () => void;
  onOpenProfile: () => void;
}) {
  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle>More in TARK</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <button
            type="button"
            onClick={onOpenBuzz}
            className="flex w-full items-start gap-4 rounded-2xl border border-border bg-surface-elevated/20 p-4 text-left transition-colors hover:border-primary/30 hover:bg-surface-elevated"
          >
            <div className="rounded-2xl bg-primary/10 p-3 text-primary">
              <Radio className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-text">Buzz</p>
              <p className="mt-1 text-sm leading-6 text-text-muted">Videos, scores, and sports storylines worth opening the app for.</p>
            </div>
          </button>

          <button
            type="button"
            onClick={onOpenPros}
            className="flex w-full items-start gap-4 rounded-2xl border border-border bg-surface-elevated/20 p-4 text-left transition-colors hover:border-primary/30 hover:bg-surface-elevated"
          >
            <div className="rounded-2xl bg-primary/10 p-3 text-primary">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-text">PROs</p>
              <p className="mt-1 text-sm leading-6 text-text-muted">Browse coaches, academies, and stringers without leaving TARK.</p>
            </div>
          </button>

          <button
            type="button"
            onClick={onOpenProfile}
            className="flex w-full items-start gap-4 rounded-2xl border border-border bg-surface-elevated/20 p-4 text-left transition-colors hover:border-primary/30 hover:bg-surface-elevated"
          >
            <div className="rounded-2xl bg-primary/10 p-3 text-primary">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-text">Me</p>
              <p className="mt-1 text-sm leading-6 text-text-muted">Profile, privacy, security, and account settings stay here.</p>
            </div>
          </button>
        </div>

        <div className="rounded-2xl border border-border bg-surface-elevated/20 p-4">
          <p className="font-semibold text-text">
            {memberships.length > 0
              ? `${memberships.length} organization${memberships.length === 1 ? '' : 's'} available`
              : 'Need a private organization later?'}
          </p>
          <p className="mt-2 text-sm text-text-muted">
            Join an organization when a coach or academy invites you. Becoming a PRO stays available here too, but Play stays the default.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button onClick={onJoinOrg}>Join Organization</Button>
          <Button variant="outline" onClick={onBecomePro}>Become a PRO</Button>
          <Button variant="ghost" onClick={onOpenProfile}>Open Me</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ProviderWorkspacePanel({
  workTabLabel,
  directoryLabel,
  provider,
  directoryCount,
  pendingInvitationCount,
  onOpenDirectory,
  onOpenProfile,
}: {
  workTabLabel: string;
  directoryLabel: string;
  provider: {
    displayName: string;
    providerTypes: string[];
    publicationStatus?: string | null;
    publiclyVisible: boolean;
    city?: string | null;
    state?: string | null;
  } | null;
  directoryCount: number;
  pendingInvitationCount: number;
  onOpenDirectory: () => void;
  onOpenProfile: () => void;
}) {
  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle>Private tools</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-2xl border border-border bg-surface-elevated/20 p-4">
          <p className="font-semibold text-text">{provider?.displayName || 'Public profile not linked yet'}</p>
          <p className="mt-2 text-sm text-text-muted">
            {provider
              ? `${provider.providerTypes.join(', ')} • ${provider.publicationStatus || 'Draft'}${provider.publiclyVisible ? ' • Public' : ' • Private'}`
              : `${workTabLabel} is ready, but the public profile still needs to be linked or completed.`}
          </p>
          {provider?.city || provider?.state ? (
            <p className="mt-2 text-xs uppercase tracking-[0.18em] text-text-subtle">
              {[provider.city, provider.state].filter(Boolean).join(', ')}
            </p>
          ) : null}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <MiniMetric label={directoryLabel} value={directoryCount} />
          <MiniMetric label="Pending Invites" value={pendingInvitationCount} />
        </div>

        <div className="flex flex-wrap gap-3">
          <Button onClick={onOpenDirectory}>Open {directoryLabel}</Button>
          <Button variant="ghost" onClick={onOpenProfile}>Open Me</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function WorkspaceOverviewPanel({
  memberships,
  activeTenantId,
  onOpenProfile,
}: {
  memberships: TenantMembershipSummary[];
  activeTenantId: number | null;
  onOpenProfile: () => void;
}) {
  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle>Available views</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <WorkspaceRow
            title="Play"
            subtitle="Squads, Play, PROs, and Buzz"
            active={activeTenantId == null}
          />

          {memberships.slice(0, 4).map((membership) =>
            membership.tenant?.id ? (
              <WorkspaceRow
                key={membership.membershipId}
                title={membership.tenant.displayName}
                subtitle={[membership.roleCode, membership.tenant.workTabLabel, membership.tenant.directoryTabLabel]
                  .filter(Boolean)
                  .join(' • ')}
                active={activeTenantId === membership.tenant.id}
              />
            ) : null
          )}
        </div>

        <Button variant="outline" onClick={onOpenProfile}>Manage views in Me</Button>
      </CardContent>
    </Card>
  );
}

function WorkspaceRow({
  title,
  subtitle,
  active,
}: {
  title: string;
  subtitle: string;
  active: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-surface-elevated/20 px-4 py-3">
      <div>
        <p className="font-semibold text-text">{title}</p>
        <p className="mt-1 text-sm text-text-muted">{subtitle}</p>
      </div>
      <div className={active ? 'rounded-full bg-primary px-3 py-1 text-xs font-semibold uppercase tracking-wide [color:var(--color-on-primary)]' : 'rounded-full border border-border px-3 py-1 text-xs font-semibold uppercase tracking-wide text-text-subtle'}>
        {active ? 'Active' : 'Available'}
      </div>
    </div>
  );
}

function MiniMetric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-2xl border border-border bg-surface-elevated/20 p-4">
      <p className="text-xs uppercase tracking-[0.18em] text-text-subtle">{label}</p>
      <p className="mt-2 text-2xl font-semibold tracking-tight text-text">{value}</p>
    </div>
  );
}
